// src/tabs/MotoboyTab.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Bike, Navigation, MapPin, CheckCircle, Clock, CheckSquare, Square, ShieldAlert, Award, FileText, DollarSign, Calendar, RefreshCw, BarChart2, Plus, LogOut, Camera, Eye, History, ArrowRight, Search, Trash2, Archive, RotateCcw, Edit, Settings } from "lucide-react";
import { pb } from "../services/pocketbase";
import { financeiroIntegracaoService } from "../services/financeiroIntegracaoService";

const OFFICE_ADDRESS = {
  cep: "03533-001",
  logradouro: "Av. Waldemar Carlos Pereira",
  numero: "179",
  complemento: "Sala 6",
  bairro: "Vila Talarico",
  cidade: "São Paulo",
  estado: "SP",
  referencia: "Sempre Joias Escritório"
};

const WORKSHOP_ADDRESS = {
  cep: "03512-050",
  logradouro: "Rua Hercilia",
  numero: "94",
  complemento: "",
  bairro: "Vila Matilde",
  cidade: "São Paulo",
  estado: "SP",
  referencia: "Oficina Sempre Joias"
};

const fetchGeocodeUrl = async (query, targetCity) => {
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`;
  try {
    const res = await fetch(url, {
      headers: {
        "User-Agent": "Antigravity-SempreJoiasApp/1.0"
      }
    });
    const data = await res.json();
    if (data && data.length > 0) {
      const match = data.find(r => {
        if (!r.address) return false;
        const city = r.address.city || r.address.town || r.address.village || r.address.municipality || r.address.city_district || "";
        return city.toLowerCase().includes(targetCity.toLowerCase()) || targetCity.toLowerCase().includes(city.toLowerCase());
      });
      if (match) {
        return {
          lat: parseFloat(match.lat),
          lon: parseFloat(match.lon)
        };
      }
    }
  } catch (err) {
    console.error("OSM Geocoding failed for query:", query, err);
  }
  return null;
};

const geocodeAddress = async (addr) => {
  if (!addr || !addr.logradouro || !addr.cidade) return null;
  
  const targetCity = addr.cidade;

  let query = `${addr.logradouro}, ${addr.numero || ""}, ${addr.cidade} - ${addr.estado || ""}, Brasil`;
  let coords = await fetchGeocodeUrl(query, targetCity);
  if (coords) return coords;

  if (addr.bairro) {
    query = `${addr.logradouro}, ${addr.bairro}, ${addr.cidade} - ${addr.estado || ""}, Brasil`;
    coords = await fetchGeocodeUrl(query, targetCity);
    if (coords) return coords;
  }

  query = `${addr.logradouro}, ${addr.cidade}, Brasil`;
  coords = await fetchGeocodeUrl(query, targetCity);
  if (coords) return coords;

  // Fallback final: se tudo mais falhar, pega o primeiro resultado sem validação estrita da cidade
  try {
    const fallbackUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`;
    const res = await fetch(fallbackUrl, { headers: { "User-Agent": "Antigravity-SempreJoiasApp/1.0" } });
    const data = await res.json();
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lon: parseFloat(data[0].lon)
      };
    }
  } catch (e) {
    console.error("OSM fallback failed:", e);
  }

  return null;
};

const getRouteDistanceOSRM = async (coords1, coords2) => {
  if (!coords1 || !coords2) return null;
  const url = `https://router.project-osrm.org/route/v1/driving/${coords1.lon},${coords1.lat};${coords2.lon},${coords2.lat}?overview=false`;
  try {
    const res = await fetch(url);
    const data = await res.json();
    if (data && data.routes && data.routes.length > 0) {
      return data.routes[0].distance / 1000;
    }
  } catch (err) {
    console.error("OSRM routing failed:", err);
  }
  return null;
};

const calculateHaversineDistance = (coords1, coords2) => {
  const R = 6371; // Radius of the earth in km
  const dLat = (coords2.lat - coords1.lat) * Math.PI / 180;
  const dLon = (coords2.lon - coords1.lon) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(coords1.lat * Math.PI / 180) * Math.cos(coords2.lat * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
};

const loadGoogleMapsScript = (apiKey) => {
  return new Promise((resolve) => {
    if (window.google && window.google.maps) {
      resolve(true);
      return;
    }
    const scriptId = "google-maps-api-script";
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    } else {
      const interval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(interval);
          resolve(true);
        }
      }, 100);
    }
  });
};

const getGoogleDistanceMatrix = (origin, destination, apiKey) => {
  return new Promise(async (resolve, reject) => {
    try {
      const loaded = await loadGoogleMapsScript(apiKey);
      if (!loaded || !window.google || !window.google.maps) {
        reject(new Error("Failed to load Google Maps SDK"));
        return;
      }
      const service = new window.google.maps.DistanceMatrixService();
      service.getDistanceMatrix(
        {
          origins: [origin],
          destinations: [destination],
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (response, status) => {
          if (status === "OK" && response && response.rows[0] && response.rows[0].elements[0].status === "OK") {
            const distanceMeters = response.rows[0].elements[0].distance.value;
            resolve(distanceMeters / 1000);
          } else {
            reject(new Error("Google Matrix returned non-OK status: " + status));
          }
        }
      );
    } catch (e) {
      reject(e);
    }
  });
};

const calculateDistance = async (addr1, addr2, googleApiKey = null) => {
  if (!addr1.logradouro || !addr2.logradouro) return null;
  
  const originStr = `${addr1.logradouro}, ${addr1.numero || ""}, ${addr1.cidade} - ${addr1.estado}, Brasil`;
  const destStr = `${addr2.logradouro}, ${addr2.numero || ""}, ${addr2.cidade} - ${addr2.estado}, Brasil`;

  if (googleApiKey && googleApiKey.trim().length > 10 && !googleApiKey.startsWith("http")) {
    try {
      const dist = await getGoogleDistanceMatrix(originStr, destStr, googleApiKey);
      return dist;
    } catch (err) {
      console.warn("Google Distance Matrix SDK failed, falling back to OSRM...", err);
    }
  }

  const coords1 = await geocodeAddress(addr1);
  const coords2 = await geocodeAddress(addr2);
  if (coords1 && coords2) {
    const dist = await getRouteDistanceOSRM(coords1, coords2);
    if (dist !== null) return dist;
    return calculateHaversineDistance(coords1, coords2);
  }

  return null;
};

const getBlankAddress = () => ({
  cep: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  estado: "",
  referencia: ""
});

export default function MotoboyTab({ user }) {
  const isMotoboyRole = user?.role === "motoboy";
  const isAdmin = user?.role !== "motoboy";

  const [deliveries, setDeliveries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tab navigation for admin
  const [adminActiveTab, setAdminActiveTab] = useState("entregas"); // "entregas" | "conta_corrente" | "dashboard" | "arquivo"
  // Tab navigation for motoboy
  const [motoboyActiveTab, setMotoboyActiveTab] = useState("pendentes"); // "pendentes" | "realizadas"

  // Filter States
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedMotoboyId, setSelectedMotoboyId] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Account statement motoboy selection
  const [statementMotoboyId, setStatementMotoboyId] = useState("");

  // Modais
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const [activeDelivery, setActiveDelivery] = useState(null);

  // Form: Confirm Delivery
  const [recebedor, setRecebedor] = useState("");
  const [taxa, setTaxa] = useState("15");
  const [obs, setObs] = useState("");
  const [comprovanteFile, setComprovanteFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form: Register Payment
  const [payValue, setPayValue] = useState("");
  const [payObs, setPayObs] = useState("");

  // Motoboy Search
  const [motoboySearchTerm, setMotoboySearchTerm] = useState("");

  // External Motoboy Assign
  const [isExternalAssignModalOpen, setIsExternalAssignModalOpen] = useState(false);
  const [solicitadoPor, setSolicitadoPor] = useState("cliente"); // "cliente" | "empresa"
  const [externalObs, setExternalObs] = useState("");

  // External Motoboy Edit
  const [isExternalEditModalOpen, setIsExternalEditModalOpen] = useState(false);
  const [editDelivery, setEditDelivery] = useState(null);
  const [editStatus, setEditStatus] = useState("pendente");
  const [editSolicitadoPor, setEditSolicitadoPor] = useState("cliente");
  const [editExternalObs, setEditExternalObs] = useState("");
  const [editRecebedor, setEditRecebedor] = useState("");
  const [editTaxa, setEditTaxa] = useState("15");
  const [editComprovanteFile, setEditComprovanteFile] = useState(null);

  // General Delivery Edit Modal
  const [isGeneralEditModalOpen, setIsGeneralEditModalOpen] = useState(false);
  const [generalEditDelivery, setGeneralEditDelivery] = useState(null);
  const [generalEditOrderNumber, setGeneralEditOrderNumber] = useState("");
  const [generalEditCustomerName, setGeneralEditCustomerName] = useState("");
  const [generalEditSolicitanteId, setGeneralEditSolicitanteId] = useState("");
  const [generalEditMotoboyId, setGeneralEditMotoboyId] = useState("");
  const [generalEditStatus, setGeneralEditStatus] = useState("pendente");
  const [generalEditObs, setGeneralEditObs] = useState("");
  const [generalEditRecebedor, setGeneralEditRecebedor] = useState("");
  const [generalEditTaxa, setGeneralEditTaxa] = useState("15");
  const [generalEditRetiradaAddress, setGeneralEditRetiradaAddress] = useState(getBlankAddress());
  const [generalEditEntregaAddress, setGeneralEditEntregaAddress] = useState(getBlankAddress());

  // Modal de Cadastro de Entrega Manual
  const [isManualInsertModalOpen, setIsManualInsertModalOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [manualOrderNumber, setManualOrderNumber] = useState("");
  const [manualCustomerName, setManualCustomerName] = useState("");
  const [manualSolicitanteId, setManualSolicitanteId] = useState("");
  const [manualMotoboyId, setManualMotoboyId] = useState("");
  const [manualObs, setManualObs] = useState("");
  const [manualRetiradaAddress, setManualRetiradaAddress] = useState(getBlankAddress());
  const [manualEntregaAddress, setManualEntregaAddress] = useState(getBlankAddress());
  const [savedDestinations, setSavedDestinations] = useState([]);

  // Estados adicionais para cálculo de distância, valores e gerenciamento de salvos
  const [manualDistanceKm, setManualDistanceKm] = useState(0);
  const [manualSuggestedValue, setManualSuggestedValue] = useState(0);
  const [manualTaxa, setManualTaxa] = useState("15");
  const [manualIsDiretaUrgente, setManualIsDiretaUrgente] = useState(false);

  const [generalEditDistanceKm, setGeneralEditDistanceKm] = useState(0);
  const [generalEditSuggestedValue, setGeneralEditSuggestedValue] = useState(0);
  const [generalEditIsDiretaUrgente, setGeneralEditIsDiretaUrgente] = useState(false);
  const [motoboyTimeFilter, setMotoboyTimeFilter] = useState("hoje");

  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false);
  const [isCalculatingDistanceEdit, setIsCalculatingDistanceEdit] = useState(false);

  const [isManageDestinationsModalOpen, setIsManageDestinationsModalOpen] = useState(false);
  const [editingDestinationId, setEditingDestinationId] = useState(null);
  const [editDestForm, setEditDestForm] = useState({ nome: "", endereco: getBlankAddress() });
  const [googleMapsKey, setGoogleMapsKey] = useState(localStorage.getItem("semprejoias_google_maps_api_key") || "");

  const openManualInsertModal = () => {
    setManualOrderNumber("MN-" + Date.now().toString().slice(-6));
    setManualCustomerName("");
    setManualSolicitanteId(user?.id || "");
    setManualMotoboyId("");
    setManualObs("");
    setManualRetiradaAddress(getBlankAddress());
    setManualEntregaAddress(getBlankAddress());
    setManualDistanceKm(0);
    setManualSuggestedValue(0);
    setManualTaxa("15");
    setManualIsDiretaUrgente(false);
    setIsManualInsertModalOpen(true);
  };

  // CEP Auto-complete
  const fetchAddressByCep = async (cep, type) => {
    const cleanedCep = cep.replace(/\D/g, "");
    if (cleanedCep.length !== 8) return;
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanedCep}/json/`);
      const data = await response.json();
      if (data.erro) return;

      const updatedFields = {
        logradouro: data.logradouro || "",
        bairro: data.bairro || "",
        cidade: data.localidade || "",
        estado: data.uf || "",
        cep: data.cep || cep
      };

      if (type === "retirada") {
        setManualRetiradaAddress(prev => ({ ...prev, ...updatedFields }));
      } else {
        setManualEntregaAddress(prev => ({ ...prev, ...updatedFields }));
      }
    } catch (err) {
      console.error("Erro ao buscar CEP:", err);
    }
  };

  const handleDistanceChange = (val, type) => {
    const dist = parseFloat(val) || 0;
    if (type === "manual") {
      setManualDistanceKm(dist);
      const boy = allUsers.find(u => u.id === manualMotoboyId);
      let rate = 0;
      if (boy) {
        rate = boy.taxa_motoboy || parseFloat(localStorage.getItem(`motoboy_rate_${boy.id}`)) || 0;
      }
      let suggested = dist * rate;
      if (manualIsDiretaUrgente) suggested *= 2;
      setManualSuggestedValue(suggested);
      setManualTaxa(suggested > 0 ? String(Math.round(suggested)) : "15");
    } else {
      setGeneralEditDistanceKm(dist);
      const boy = allUsers.find(u => u.id === generalEditMotoboyId);
      let rate = 0;
      if (boy) {
        rate = boy.taxa_motoboy || parseFloat(localStorage.getItem(`motoboy_rate_${boy.id}`)) || 0;
      }
      let suggested = dist * rate;
      if (generalEditIsDiretaUrgente) suggested *= 2;
      setGeneralEditSuggestedValue(suggested);
      setGeneralEditTaxa(suggested > 0 ? String(Math.round(suggested)) : "15");
    }
  };

  const handleToggleDiretaUrgente = (checked, type) => {
    if (type === "manual") {
      setManualIsDiretaUrgente(checked);
      const boy = allUsers.find(u => u.id === manualMotoboyId);
      let rate = 0;
      if (boy) {
        rate = boy.taxa_motoboy || parseFloat(localStorage.getItem(`motoboy_rate_${boy.id}`)) || 0;
      }
      let suggested = manualDistanceKm * rate;
      if (checked) suggested *= 2;
      setManualSuggestedValue(suggested);
      setManualTaxa(suggested > 0 ? String(Math.round(suggested)) : "15");
    } else {
      setGeneralEditIsDiretaUrgente(checked);
      const boy = allUsers.find(u => u.id === generalEditMotoboyId);
      let rate = 0;
      if (boy) {
        rate = boy.taxa_motoboy || parseFloat(localStorage.getItem(`motoboy_rate_${boy.id}`)) || 0;
      }
      let suggested = generalEditDistanceKm * rate;
      if (checked) suggested *= 2;
      setGeneralEditSuggestedValue(suggested);
      setGeneralEditTaxa(suggested > 0 ? String(Math.round(suggested)) : "15");
    }
  };

  const triggerDistanceAndPriceCalculation = async (type = "manual_insert") => {
    const isManual = type === "manual_insert";
    const start = isManual ? manualRetiradaAddress : generalEditRetiradaAddress;
    const end = isManual ? manualEntregaAddress : generalEditEntregaAddress;
    const motoboyId = isManual ? manualMotoboyId : generalEditMotoboyId;

    if (!start.logradouro || !start.cidade || !end.logradouro || !end.cidade) {
      return;
    }

    if (isManual) {
      setIsCalculatingDistance(true);
    } else {
      setIsCalculatingDistanceEdit(true);
    }

    try {
      const googleApiKey = localStorage.getItem("semprejoias_google_maps_api_key");
      const distance = await calculateDistance(start, end, googleApiKey);
      
      if (distance !== null) {
        const roundedDist = Math.round(distance * 10) / 10;
        const boy = allUsers.find(u => u.id === motoboyId);
        let rate = 0;
        if (boy) {
          rate = boy.taxa_motoboy || parseFloat(localStorage.getItem(`motoboy_rate_${boy.id}`)) || 0;
        }
        let suggestedVal = roundedDist * rate;
        
        if (isManual) {
          if (manualIsDiretaUrgente) suggestedVal *= 2;
          setManualDistanceKm(roundedDist);
          setManualSuggestedValue(suggestedVal);
          setManualTaxa(suggestedVal > 0 ? String(Math.round(suggestedVal)) : "15");
        } else {
          if (generalEditIsDiretaUrgente) suggestedVal *= 2;
          setGeneralEditDistanceKm(roundedDist);
          setGeneralEditSuggestedValue(suggestedVal);
          setGeneralEditTaxa(suggestedVal > 0 ? String(Math.round(suggestedVal)) : "15");
        }
      }
    } catch (err) {
      console.error("Error calculating suggested value:", err);
    } finally {
      if (isManual) {
        setIsCalculatingDistance(false);
      } else {
        setIsCalculatingDistanceEdit(false);
      }
    }
  };

  // Debounced effect for manual insert distance calculation
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      triggerDistanceAndPriceCalculation("manual_insert");
    }, 1200);
    return () => clearTimeout(delayDebounce);
  }, [
    manualRetiradaAddress.logradouro,
    manualRetiradaAddress.numero,
    manualRetiradaAddress.cidade,
    manualEntregaAddress.logradouro,
    manualEntregaAddress.numero,
    manualEntregaAddress.cidade,
    manualMotoboyId
  ]);

  // Debounced effect for edit distance calculation
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      triggerDistanceAndPriceCalculation("edit");
    }, 1200);
    return () => clearTimeout(delayDebounce);
  }, [
    generalEditRetiradaAddress.logradouro,
    generalEditRetiradaAddress.numero,
    generalEditRetiradaAddress.cidade,
    generalEditEntregaAddress.logradouro,
    generalEditEntregaAddress.numero,
    generalEditEntregaAddress.cidade,
    generalEditMotoboyId
  ]);

  const handleCreateManualDelivery = async (e) => {
    e.preventDefault();
    if (!manualCustomerName.trim()) return alert("O nome do cliente/destinatário é obrigatório.");
    
    setIsSubmitting(true);
    try {
      const finalObs = manualIsDiretaUrgente 
        ? `[DIRETA/URGENTE] ${manualObs.trim()}`.trim() 
        : manualObs.trim();

      const deliveryData = {
        pedido_id: "",
        numero_pedido: manualOrderNumber.trim() || "MANUAL",
        cliente_nome: manualCustomerName.trim(),
        endereco: manualEntregaAddress,
        endereco_retirada: manualRetiradaAddress,
        motoboy_id: manualMotoboyId || "",
        status: "pendente",
        data_solicitacao: new Date().toISOString(),
        solicitante_id: manualSolicitanteId || "",
        observacoes: finalObs,
        taxa_entrega: Number(manualTaxa) || 0,
        distancia_km: Number(manualDistanceKm) || 0,
        valor_sugerido: Number(manualSuggestedValue) || 0,
        direta_urgente: manualIsDiretaUrgente
      };

      let newDelivery;
      try {
        newDelivery = await pb.collection("motoboy_entregas").create(deliveryData);
      } catch (dbErr) {
        console.warn("Creation failed with suggested/urgent fields, retrying without them:", dbErr);
        const { distancia_km, valor_sugerido, direta_urgente, ...cleanedData } = deliveryData;
        newDelivery = await pb.collection("motoboy_entregas").create(cleanedData);
      }

      setDeliveries(prev => [newDelivery, ...prev]);
      setIsManualInsertModalOpen(false);
      alert("Entrega manual criada com sucesso!");
    } catch (err) {
      alert("Erro ao criar entrega manual: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveFrequentDestination = async (type = "entrega", isEdit = false) => {
    let addr;
    if (type === "retirada") {
      addr = isEdit ? generalEditRetiradaAddress : manualRetiradaAddress;
    } else {
      addr = isEdit ? generalEditEntregaAddress : manualEntregaAddress;
    }

    if (!addr.logradouro || !addr.numero) {
      return alert("Preencha ao menos Rua/Logradouro e Número para salvar o endereço.");
    }
    const name = window.prompt("Digite um nome para identificar este endereço salvo (Ex: Fornecedor Joia Rápida):");
    if (!name || !name.trim()) return;

    try {
      const record = await pb.collection("motoboy_destinos").create({
        nome: name.trim(),
        endereco: addr
      });
      setSavedDestinations(prev => [...prev, record].sort((a, b) => a.nome.localeCompare(b.nome)));
      alert("Endereço salvo com sucesso!");
    } catch (err) {
      alert("Erro ao salvar endereço frequente: " + err.message);
    }
  };

  const handleStartEditDestination = (d) => {
    setEditingDestinationId(d.id);
    setEditDestForm({
      nome: d.nome || "",
      endereco: {
        cep: d.endereco?.cep || "",
        logradouro: d.endereco?.logradouro || "",
        numero: d.endereco?.numero || "",
        complemento: d.endereco?.complemento || "",
        bairro: d.endereco?.bairro || "",
        cidade: d.endereco?.cidade || "",
        estado: d.endereco?.estado || ""
      }
    });
  };

  const handleSaveEditDestination = async () => {
    if (!editDestForm.nome.trim()) return alert("Nome é obrigatório.");
    if (!editDestForm.endereco.logradouro || !editDestForm.endereco.numero) {
      return alert("Logradouro e Número são obrigatórios.");
    }
    try {
      const updated = await pb.collection("motoboy_destinos").update(editingDestinationId, {
        nome: editDestForm.nome.trim(),
        endereco: editDestForm.endereco
      });
      setSavedDestinations(prev => prev.map(d => d.id === editingDestinationId ? updated : d).sort((a, b) => a.nome.localeCompare(b.nome)));
      setEditingDestinationId(null);
      alert("Endereço atualizado com sucesso!");
    } catch (err) {
      alert("Erro ao editar endereço salvo: " + err.message);
    }
  };

  const handleDeleteDestination = async (id) => {
    if (!window.confirm("Deseja realmente excluir este endereço salvo?")) return;
    try {
      await pb.collection("motoboy_destinos").delete(id);
      setSavedDestinations(prev => prev.filter(d => d.id !== id));
      alert("Endereço excluído com sucesso!");
    } catch (err) {
      alert("Erro ao excluir endereço: " + err.message);
    }
  };

  // Load everything
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [delivRecords, payRecords, userRecords] = await Promise.all([
        pb.collection("motoboy_entregas").getFullList({ sort: "-data_solicitacao" }),
        pb.collection("motoboy_pagamentos").getFullList({ sort: "-data_pagamento" }),
        pb.collection("users").getFullList({ sort: "name" })
      ]);

      // Garante a existência do usuário virtual "MOTOBOY EXTERNO" (apenas para Admin)
      let externalBoy = userRecords.find(u => 
        (u.name && u.name.trim().toUpperCase() === "MOTOBOY EXTERNO") || 
        (u.username && u.username.toLowerCase().startsWith("externo"))
      );
      if (!externalBoy && isAdmin) {
        try {
          externalBoy = await pb.collection("users").create({
            username: "externo_" + Math.random().toString(36).substring(2, 7),
            name: "MOTOBOY EXTERNO",
            email: `externo_${Date.now()}@semprejoias.com`,
            password: "default_password_externo_123",
            passwordConfirm: "default_password_externo_123",
            role: "motoboy"
          });
          userRecords.push(externalBoy);
        } catch (err) {
          console.error("Erro ao criar usuário virtual MOTOBOY EXTERNO:", err);
        }
      }

      setDeliveries(delivRecords);
      setPayments(payRecords);
      setAllUsers(userRecords);

      // Carregar Google Maps API Key do PocketBase (coleção motoboy_configs)
      try {
        const configs = await pb.collection("motoboy_configs").getFullList();
        if (configs.length > 0) {
          const key = configs[0].google_maps_api_key || "";
          setGoogleMapsKey(key);
          localStorage.setItem("semprejoias_google_maps_api_key", key);
        }
      } catch (err) {
        console.warn("⚠️ Coleção 'motoboy_configs' não encontrada ou inacessível no PocketBase. Usando local storage fallback.");
      }
      
      try {
        const destRecords = await pb.collection("motoboy_destinos").getFullList({ sort: "nome" });
        setSavedDestinations(destRecords);
      } catch (err) {
        console.warn("⚠️ Coleção 'motoboy_destinos' não encontrada ou inacessível no PocketBase:", err.message);
        setSavedDestinations([]);
      }
      
      const seenNames = new Set();
      const filteredBoys = userRecords
        .filter(u => u.role === "motoboy" || u.role === "entregador")
        .filter(u => {
          if (!u.name) return true;
          const normalized = u.name.trim().toUpperCase();
          if (seenNames.has(normalized)) return false;
          seenNames.add(normalized);
          return true;
        });

      const regularBoys = filteredBoys.filter(m => m.name !== "MOTOBOY EXTERNO");

      if (user?.role === "motoboy" && user?.id) {
        setStatementMotoboyId(user.id);
      } else if (regularBoys.length > 0 && !statementMotoboyId) {
        setStatementMotoboyId(regularBoys[0].id);
      }
    } catch (e) {
      console.error("Erro loading motoboy portal data:", e);
    } finally {
      setLoading(false);
    }
  }, [statementMotoboyId, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Real-time subscriptions for deliveries, payments, and users
  useEffect(() => {
    // Subscribe to deliveries
    pb.collection("motoboy_entregas").subscribe("*", (e) => {
      setDeliveries(prev => {
        if (e.action === "delete") {
          return prev.filter(d => d.id !== e.record.id);
        }
        if (e.action === "create") {
          if (prev.some(d => d.id === e.record.id)) return prev;
          return [e.record, ...prev];
        }
        if (e.action === "update") {
          return prev.map(d => d.id === e.record.id ? e.record : d);
        }
        return prev;
      });
    });

    // Subscribe to payments
    pb.collection("motoboy_pagamentos").subscribe("*", (e) => {
      setPayments(prev => {
        if (e.action === "delete") {
          return prev.filter(p => p.id !== e.record.id);
        }
        if (e.action === "create") {
          if (prev.some(p => p.id === e.record.id)) return prev;
          return [e.record, ...prev];
        }
        if (e.action === "update") {
          return prev.map(p => p.id === e.record.id ? e.record : p);
        }
        return prev;
      });
    });

    // Subscribe to users
    pb.collection("users").subscribe("*", (e) => {
      setAllUsers(prev => {
        if (e.action === "delete") {
          return prev.filter(u => u.id !== e.record.id);
        }
        if (e.action === "create") {
          if (prev.some(u => u.id === e.record.id)) return prev;
          return [...prev, e.record].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        }
        if (e.action === "update") {
          return prev.map(u => u.id === e.record.id ? e.record : u);
        }
        return prev;
      });
    });

    return () => {
      pb.collection("motoboy_entregas").unsubscribe("*").catch(() => {});
      pb.collection("motoboy_pagamentos").unsubscribe("*").catch(() => {});
      pb.collection("users").unsubscribe("*").catch(() => {});
    };
  }, []);

  // Format Address for maps
  const formatAddress = (addr) => {
    if (!addr) return "";
    return `${addr.logradouro || ""}, ${addr.numero || ""}, ${addr.bairro || ""}, ${addr.cidade || ""}-${addr.estado || ""}`;
  };

  // Google Maps single link
  const getMapsLink = (addr) => {
    const full = formatAddress(addr);
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(full)}`;
  };

  // Optimized Route waypoints
  const getOptimizedRouteLink = (pendingDelivs) => {
    if (pendingDelivs.length === 0) return "#";
    const addresses = pendingDelivs.map(d => formatAddress(d.endereco)).filter(Boolean);
    if (addresses.length === 1) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addresses[0])}`;
    }
    const waypoints = addresses.slice(0, -1).map(addr => encodeURIComponent(addr)).join("|");
    const destination = encodeURIComponent(addresses[addresses.length - 1]);
    return `https://www.google.com/maps/dir/?api=1&waypoints=${waypoints}&destination=${destination}&travelmode=driving`;
  };

  // --- ACTIONS ---

  // Motoboy starts delivery
  const handleStartDelivery = async (deliv) => {
    if (!window.confirm("Marcar que você saiu para entrega deste pedido?")) return;
    try {
      const isAvulso = !deliv.numero_pedido || deliv.numero_pedido === "AVULSO";
      let recordsToUpdate = [deliv];
      if (!isAvulso) {
        recordsToUpdate = deliveries.filter(d => 
          d.numero_pedido === deliv.numero_pedido &&
          d.status !== "entregue" &&
          d.status !== "cancelado"
        );
      }

      const updatedRecords = await Promise.all(
        recordsToUpdate.map(async (d) => {
          return pb.collection("motoboy_entregas").update(d.id, {
            status: "saiu_para_entrega",
            data_saida: new Date().toISOString()
          });
        })
      );

      setDeliveries(prev => prev.map(d => {
        const match = updatedRecords.find(ur => ur.id === d.id);
        return match ? match : d;
      }));

      alert("Entrega marcada como 'Saiu para Entrega'!");
    } catch (e) {
      alert("Erro ao atualizar status: " + e.message);
    }
  };

  // Confirm delivery
  const handleConfirmDelivery = async (e) => {
    e.preventDefault();
    if (!activeDelivery) return;
    if (!recebedor.trim()) return alert("Digite o nome de quem recebeu.");
    if (!taxa || Number(taxa) <= 0) return alert("Digite um valor válido de taxa.");

    setIsSubmitting(true);
    try {
      const isAvulso = !activeDelivery.numero_pedido || activeDelivery.numero_pedido === "AVULSO";
      let recordsToUpdate = [activeDelivery];
      if (!isAvulso) {
        recordsToUpdate = deliveries.filter(d => 
          d.numero_pedido === activeDelivery.numero_pedido &&
          d.status !== "entregue" &&
          d.status !== "cancelado"
        );
      }

      const updatedRecords = await Promise.all(
        recordsToUpdate.map(async (d) => {
          const formData = new FormData();
          formData.append("status", "entregue");
          formData.append("data_entrega", new Date().toISOString());
          formData.append("nome_recebedor", recebedor.trim());
          formData.append("taxa_entrega", Number(taxa));
          formData.append("observacoes", obs.trim());
          formData.append("pago", "false"); // Custom flag para conta-corrente
          
          if (comprovanteFile) {
            formData.append("foto_comprovante", comprovanteFile);
          }
          return pb.collection("motoboy_entregas").update(d.id, formData);
        })
      );

      setDeliveries(prev => prev.map(d => {
        const match = updatedRecords.find(ur => ur.id === d.id);
        return match ? match : d;
      }));

      setIsConfirmModalOpen(false);
      
      // Reset form
      setRecebedor("");
      setTaxa("15");
      setObs("");
      setComprovanteFile(null);
      alert("Entrega registrada com sucesso!");
    } catch (err) {
      alert("Erro ao confirmar entrega: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Postpone delivery
  const handlePostponeDelivery = async (deliv) => {
    const reason = window.prompt("Motivo do adiamento da entrega:");
    if (reason === null) return;
    if (!reason.trim()) return alert("O motivo do adiamento é obrigatório.");

    try {
      const isAvulso = !deliv.numero_pedido || deliv.numero_pedido === "AVULSO";
      let recordsToUpdate = [deliv];
      if (!isAvulso) {
        recordsToUpdate = deliveries.filter(d => 
          d.numero_pedido === deliv.numero_pedido &&
          d.status !== "entregue" &&
          d.status !== "cancelado"
        );
      }

      const updatedRecords = await Promise.all(
        recordsToUpdate.map(async (d) => {
          return pb.collection("motoboy_entregas").update(d.id, {
            status: "adiada",
            observacoes: `Adiada: ${reason.trim()} | Obs: ${d.observacoes || ""}`
          });
        })
      );

      setDeliveries(prev => prev.map(d => {
        const match = updatedRecords.find(ur => ur.id === d.id);
        return match ? match : d;
      }));

      alert("Entrega adiada.");
    } catch (e) {
      alert("Erro: " + e.message);
    }
  };

  // Manager assigns motoboy
  const handleAssignMotoboy = async (deliv) => {
    setActiveDelivery(deliv);
    setIsAssignModalOpen(true);
  };

  const submitAssign = async (motoboyId) => {
    if (!activeDelivery) return;
    try {
      const isAvulso = !activeDelivery.numero_pedido || activeDelivery.numero_pedido === "AVULSO";
      let recordsToUpdate = [activeDelivery];
      if (!isAvulso) {
        recordsToUpdate = deliveries.filter(d => 
          d.numero_pedido === activeDelivery.numero_pedido &&
          d.status !== "entregue" &&
          d.status !== "cancelado"
        );
      }

      const updatedRecords = await Promise.all(
        recordsToUpdate.map(async (d) => {
          return pb.collection("motoboy_entregas").update(d.id, {
            motoboy_id: motoboyId
          });
        })
      );

      setDeliveries(prev => prev.map(d => {
        const match = updatedRecords.find(ur => ur.id === d.id);
        return match ? match : d;
      }));

      setIsAssignModalOpen(false);
      alert("Motoboy atribuído com sucesso!");
    } catch (e) {
      alert("Erro ao atribuir motoboy: " + e.message);
    }
  };

  const submitExternalAssign = async (e) => {
    e.preventDefault();
    if (!activeDelivery) return;
    
    const externalBoy = motoboys.find(m => m.name === "MOTOBOY EXTERNO");
    if (!externalBoy) return alert("Erro: Usuário virtual MOTOBOY EXTERNO não encontrado.");

    try {
      const solicitadoLabel = solicitadoPor === "cliente" ? "Cliente" : "Nós / Empresa";
      const finalObs = `[Terceirizado - Solicitado por: ${solicitadoLabel}] ${externalObs.trim() ? `Obs: ${externalObs.trim()} | ` : ""}${activeDelivery.observacoes || ""}`;

      const isAvulso = !activeDelivery.numero_pedido || activeDelivery.numero_pedido === "AVULSO";
      let recordsToUpdate = [activeDelivery];
      if (!isAvulso) {
        recordsToUpdate = deliveries.filter(d => 
          d.numero_pedido === activeDelivery.numero_pedido &&
          d.status !== "entregue" &&
          d.status !== "cancelado"
        );
      }

      const updatedRecords = await Promise.all(
        recordsToUpdate.map(async (d) => {
          return pb.collection("motoboy_entregas").update(d.id, {
            motoboy_id: externalBoy.id,
            observacoes: finalObs
          });
        })
      );

      setDeliveries(prev => prev.map(d => {
        const match = updatedRecords.find(ur => ur.id === d.id);
        return match ? match : d;
      }));

      setIsExternalAssignModalOpen(false);
      setExternalObs("");
      setSolicitadoPor("cliente");
      alert("Motoboy Externo atribuído com sucesso!");
    } catch (e) {
      alert("Erro ao atribuir motoboy externo: " + e.message);
    }
  };

  const parseExternalObs = (obsString) => {
    if (!obsString) {
      return { solicitadoPor: "cliente", obsText: "" };
    }
    
    let solicitadoPor = "cliente";
    if (obsString.includes("Solicitado por: Nós / Empresa") || obsString.includes("Solicitado por: empresa")) {
      solicitadoPor = "empresa";
    }
    
    // Extract observation text
    let obsText = "";
    const obsMatch = obsString.match(/\[Terceirizado - Solicitado por:.*?\]\s*(?:Obs:\s*(.*?)(?:\s*\||$))?/i);
    if (obsMatch && obsMatch[1]) {
      obsText = obsMatch[1].trim();
    } else {
      const bracketMatch = obsString.match(/\[Terceirizado - Solicitado por:.*?\](.*)/i);
      if (bracketMatch && bracketMatch[1]) {
        let remaining = bracketMatch[1].trim();
        if (remaining.startsWith("Obs:")) {
          remaining = remaining.substring(4).trim();
        }
        if (remaining.includes("|")) {
          remaining = remaining.split("|")[0].trim();
        }
        obsText = remaining;
      }
    }
    
    return { solicitadoPor, obsText };
  };

  const handleOpenExternalEdit = (del) => {
    setEditDelivery(del);
    setEditStatus(del.status || "pendente");
    
    const { solicitadoPor, obsText } = parseExternalObs(del.observacoes);
    setEditSolicitadoPor(solicitadoPor);
    setEditExternalObs(obsText);
    
    setEditRecebedor(del.nome_recebedor || "");
    setEditTaxa(del.taxa_entrega || "15");
    setEditComprovanteFile(null);
    setIsExternalEditModalOpen(true);
  };

  const submitExternalEdit = async (e) => {
    e.preventDefault();
    if (!editDelivery) return;

    setIsSubmitting(true);
    try {
      const solicitadoLabel = editSolicitadoPor === "cliente" ? "Cliente" : "Nós / Empresa";
      
      // Limpa prefixos Terceirizado antigos das observações originais
      let originalObsCleaned = editDelivery.observacoes || "";
      originalObsCleaned = originalObsCleaned.replace(/\[Terceirizado - Solicitado por:.*?\]\s*(?:Obs:.*?\s*\|\s*)?/i, "");

      const finalObs = `[Terceirizado - Solicitado por: ${solicitadoLabel}] ${editExternalObs.trim() ? `Obs: ${editExternalObs.trim()} | ` : ""}${originalObsCleaned}`;

      const isAvulso = !editDelivery.numero_pedido || editDelivery.numero_pedido === "AVULSO";
      let recordsToUpdate = [editDelivery];
      if (!isAvulso) {
        recordsToUpdate = deliveries.filter(d => 
          d.numero_pedido === editDelivery.numero_pedido &&
          d.status !== "entregue" &&
          d.status !== "cancelado"
        );
      }

      const updatedRecords = await Promise.all(
        recordsToUpdate.map(async (d) => {
          const formData = new FormData();
          formData.append("status", editStatus);
          formData.append("observacoes", finalObs);

          if (editStatus === "entregue") {
            formData.append("data_entrega", d.data_entrega || new Date().toISOString());
            formData.append("nome_recebedor", editRecebedor.trim() || "N/A");
            formData.append("taxa_entrega", Number(editTaxa));
            formData.append("pago", "false");
            if (editComprovanteFile) {
              formData.append("foto_comprovante", editComprovanteFile);
            }
          } else {
            formData.append("nome_recebedor", "");
            formData.append("taxa_entrega", 0);
          }

          return pb.collection("motoboy_entregas").update(d.id, formData);
        })
      );

      setDeliveries(prev => prev.map(d => {
        const match = updatedRecords.find(ur => ur.id === d.id);
        return match ? match : d;
      }));

      setIsExternalEditModalOpen(false);
      alert("Entrega terceirizada atualizada com sucesso!");
    } catch (err) {
      alert("Erro ao atualizar entrega terceirizada: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenGeneralEditModal = (del) => {
    setGeneralEditDelivery(del);
    setGeneralEditOrderNumber(del.numero_pedido || "");
    setGeneralEditCustomerName(del.cliente_nome || "");
    setGeneralEditSolicitanteId(del.solicitante_id || "");
    setGeneralEditMotoboyId(del.motoboy_id || "");
    setGeneralEditStatus(del.status || "pendente");
    setGeneralEditObs(del.observacoes || "");
    setGeneralEditRecebedor(del.nome_recebedor || "");
    setGeneralEditTaxa(del.taxa_entrega || "15");
    setGeneralEditRetiradaAddress(del.endereco_retirada || getBlankAddress());
    setGeneralEditEntregaAddress(del.endereco || getBlankAddress());
    setGeneralEditDistanceKm(del.distancia_km || 0);
    setGeneralEditSuggestedValue(del.valor_sugerido || 0);
    const isUrgent = !!del.direta_urgente || (del.observacoes && /\[(?:DIRETA\/)?URGENTE\]/i.test(del.observacoes));
    setGeneralEditIsDiretaUrgente(isUrgent);
    setIsGeneralEditModalOpen(true);
  };

  const handleCalculateSuggestedPriceForDelivery = async (del) => {
    const start = del.endereco_retirada || getBlankAddress();
    const end = del.endereco || getBlankAddress();
    if (!start.logradouro || !end.logradouro) {
      alert("A entrega #" + del.numero_pedido + " não possui endereços completos de retirada e entrega configurados.");
      return;
    }

    try {
      const googleApiKey = localStorage.getItem("semprejoias_google_maps_api_key");
      const distance = await calculateDistance(start, end, googleApiKey);
      if (distance === null) {
        alert("Não foi possível estimar a rota/distância para os endereços da entrega #" + del.numero_pedido);
        return;
      }

      const roundedDist = Math.round(distance * 10) / 10;
      const boy = allUsers.find(u => u.id === del.motoboy_id);
      let rate = 0;
      if (boy) {
        rate = boy.taxa_motoboy || parseFloat(localStorage.getItem(`motoboy_rate_${boy.id}`)) || 0;
      }
      
      const isUrgent = !!del.direta_urgente || (del.observacoes && /\[(?:DIRETA\/)?URGENTE\]/i.test(del.observacoes));
      let suggestedVal = roundedDist * rate;
      if (isUrgent) suggestedVal *= 2;

      await pb.collection("motoboy_entregas").update(del.id, {
        distancia_km: roundedDist,
        valor_sugerido: suggestedVal
      });

      alert(`Estimativa calculada para entrega #${del.numero_pedido}: ${roundedDist} KM | Sugerido: R$ ${suggestedVal.toFixed(2)}`);
      loadData();
    } catch (err) {
      alert("Erro ao calcular estimativa: " + err.message);
    }
  };

  const fetchAddressByCepForEdit = async (cep, type) => {
    const cleanedCep = cep.replace(/\D/g, "");
    if (cleanedCep.length !== 8) return;
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanedCep}/json/`);
      const data = await response.json();
      if (data.erro) return;

      const updatedFields = {
        logradouro: data.logradouro || "",
        bairro: data.bairro || "",
        cidade: data.localidade || "",
        estado: data.uf || "",
        cep: data.cep || cep
      };

      if (type === "retirada") {
        setGeneralEditRetiradaAddress(prev => ({ ...prev, ...updatedFields }));
      } else {
        setGeneralEditEntregaAddress(prev => ({ ...prev, ...updatedFields }));
      }
    } catch (err) {
      console.error("Erro ao buscar CEP no edit:", err);
    }
  };

  const handleUpdateGeneralDelivery = async (e) => {
    e.preventDefault();
    if (!generalEditCustomerName.trim()) return alert("O nome do cliente/destinatário é obrigatório.");
    
    setIsSubmitting(true);
    try {
      const idsToUpdate = generalEditDelivery.originalIds || [generalEditDelivery.id];
      const finalObsCleaned = generalEditObs.trim().replace(/^\[(?:DIRETA\/)?URGENTE\]\s*/i, "");
      const finalObs = generalEditIsDiretaUrgente 
        ? `[DIRETA/URGENTE] ${finalObsCleaned}` 
        : finalObsCleaned;
      
      await Promise.all(
        idsToUpdate.map(async (id) => {
          const updateData = {
            numero_pedido: generalEditOrderNumber.trim(),
            cliente_nome: generalEditCustomerName.trim(),
            solicitante_id: generalEditSolicitanteId || "",
            motoboy_id: generalEditMotoboyId || "",
            status: generalEditStatus,
            observacoes: finalObs,
            endereco: generalEditEntregaAddress,
            endereco_retirada: generalEditRetiradaAddress,
            distancia_km: Number(generalEditDistanceKm) || 0,
            valor_sugerido: Number(generalEditSuggestedValue) || 0,
            direta_urgente: generalEditIsDiretaUrgente
          };
          
          if (generalEditStatus === "entregue") {
            updateData.nome_recebedor = generalEditRecebedor.trim() || "N/A";
            updateData.taxa_entrega = Number(generalEditTaxa) || 15;
            if (!generalEditDelivery.data_entrega) {
              updateData.data_entrega = new Date().toISOString();
            }
          } else {
            updateData.nome_recebedor = "";
            updateData.taxa_entrega = 0;
            updateData.data_entrega = null; 
          }
          
          try {
            return await pb.collection("motoboy_entregas").update(id, updateData);
          } catch (dbErr) {
            console.warn("Update failed with direta_urgente, retrying without it:", dbErr);
            const { direta_urgente, ...cleanedUpdateData } = updateData;
            return await pb.collection("motoboy_entregas").update(id, cleanedUpdateData);
          }
        })
      );

      setIsGeneralEditModalOpen(false);
      alert("Entrega atualizada com sucesso!");
      loadData();
    } catch (err) {
      alert("Erro ao atualizar entrega: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Register payment to motoboy
  const handleRegisterPayment = async (e) => {
    e.preventDefault();
    if (user?.role !== "master") {
      alert("Apenas usuários Master podem registrar pagamentos.");
      return;
    }
    const val = parseFloat(payValue);
    if (isNaN(val) || val <= 0) return alert("Digite um valor de pagamento válido.");

    try {
      const currentBalance = motoboyLedger.balance;
      const countRecent = deliveriesSinceLastPayment.length;

      const record = await pb.collection("motoboy_pagamentos").create({
        motoboy_id: statementMotoboyId,
        periodo_inicio: new Date(Date.now() - 30*24*60*60*1000).toISOString().split("T")[0],
        periodo_fim: new Date().toISOString().split("T")[0],
        total_entregas: countRecent,
        total_taxas: currentBalance,
        valor_pago: val,
        saldo_restante: Math.max(0, currentBalance - val),
        status: val >= currentBalance ? "pago" : "pago_parcial",
        data_pagamento: new Date().toISOString(),
        pago_por: user?.id || "",
        observacoes: payObs.trim()
      });

      // Gatilho Financeiro
      try {
        const motoboyName = motoboys.find(m => m.id === statementMotoboyId)?.name || "Motoboy";
        await financeiroIntegracaoService.triggerPendingTransaction({
          origem: "motoboy_pagamentos",
          origemId: record.id,
          snapshot: record,
          dataSugerida: record.data_pagamento ? record.data_pagamento.split("T")[0] : new Date().toISOString().split("T")[0],
          valorSugeridoCentavos: Math.round(val * 100),
          descricaoSugerida: `Pgto Frete Motoboy: ${motoboyName}`,
          tipoPendencia: "estorno",
        });
      } catch (errFin) {
        console.warn("Erro ao gerar pendência financeira para pagamento de motoboy:", errFin);
      }

      setPayments(prev => [record, ...prev]);
      setIsPaymentModalOpen(false);
      setPayValue("");
      setPayObs("");
      alert("Pagamento registrado com sucesso!");
      loadData(); // recarrega
    } catch (err) {
      alert("Erro ao registrar pagamento: " + err.message);
    }
  };

  // Delete delivery and cascade to statement
  const handleDeleteDelivery = async (deliv) => {
    const orderNum = deliv.numero_pedido || "AVULSO";
    if (!window.confirm(`Tem certeza que deseja apagar permanentemente a entrega #${orderNum}? Esta ação não pode ser desfeita e terá efeito cascata na conta corrente.`)) return;
    try {
      const idsToDelete = deliv.originalIds || [deliv.id];
      await Promise.all(idsToDelete.map(id => pb.collection("motoboy_entregas").delete(id)));
      alert("Entrega apagada com sucesso!");
      loadData();
    } catch (err) {
      alert("Erro ao apagar entrega: " + err.message);
    }
  };

  // Delete past payment
  const handleDeletePayment = async (paymentId) => {
    if (user?.role !== "master") {
      alert("Apenas usuários Master podem apagar pagamentos.");
      return;
    }
    if (!window.confirm("Tem certeza que deseja apagar este pagamento? O saldo do motoboy será recalculado automaticamente.")) return;
    try {
      try {
        await financeiroIntegracaoService.triggerCancellation("motoboy_pagamentos", paymentId, user);
      } catch (errFin) {
        console.warn("Erro ao cancelar pendência financeira para exclusão de pagamento de motoboy:", errFin);
      }
      await pb.collection("motoboy_pagamentos").delete(paymentId);
      alert("Pagamento apagado com sucesso!");
      loadData();
    } catch (err) {
      alert("Erro ao apagar pagamento: " + err.message);
    }
  };

  // Archive/Unarchive delivery
  const handleArchiveDelivery = async (deliv, archiveState = true) => {
    try {
      const idsToUpdate = deliv.originalIds || [deliv.id];
      await Promise.all(
        idsToUpdate.map(id =>
          pb.collection("motoboy_entregas").update(id, {
            arquivado: archiveState
          })
        )
      );
      alert(archiveState ? "Entrega arquivada com sucesso!" : "Entrega desarquivada com sucesso!");
      loadData();
    } catch (err) {
      alert("Erro ao alterar estado de arquivamento: " + err.message + "\n\n⚠️ Certifique-se de que o campo 'arquivado' (tipo bool) foi adicionado à coleção 'motoboy_entregas' no PocketBase Admin.");
    }
  };

  // --- FILTERS & LISTS ---

  // Derive motoboys dynamically from allUsers state to support real-time updates
  const motoboys = useMemo(() => {
    const seenNames = new Set();
    return allUsers
      .filter(u => u.role === "motoboy" || u.role === "entregador")
      .filter(u => {
        if (!u.name) return true;
        const normalized = u.name.trim().toUpperCase();
        if (seenNames.has(normalized)) return false;
        seenNames.add(normalized);
        return true;
      });
  }, [allUsers]);

  // Group duplicate deliveries by order number
  const groupedDeliveries = useMemo(() => {
    const grouped = [];
    const map = new Map();

    deliveries.forEach(item => {
      const orderNumber = item.numero_pedido || "AVULSO";
      const itemPaid = item.pago === true || item.pago === "true" || item.pago === "yes";
      const itemArchived = item.arquivado === true || item.arquivado === "true" || item.arquivado === "yes";

      if (!orderNumber || orderNumber === "AVULSO") {
        grouped.push({ ...item, pago: itemPaid, arquivado: itemArchived, originalIds: [item.id] });
      } else {
        if (!map.has(orderNumber)) {
          map.set(orderNumber, { ...item, pago: itemPaid, arquivado: itemArchived, originalIds: [item.id] });
        } else {
          const existing = map.get(orderNumber);
          existing.originalIds.push(item.id);
          
          // Strategy for merging:
          // 1. If current item has motoboy_id assigned, use it
          if (item.motoboy_id && !existing.motoboy_id) {
            existing.motoboy_id = item.motoboy_id;
          }
          
          // 2. Use the most advanced status
          const statusPriority = {
            "cancelada": 0,
            "pendente": 1,
            "adiada": 2,
            "saiu_para_entrega": 3,
            "entregue": 4
          };
          if (statusPriority[item.status] > statusPriority[existing.status]) {
            existing.status = item.status;
            existing.data_saida = item.data_saida || existing.data_saida;
            existing.data_entrega = item.data_entrega || existing.data_entrega;
            existing.nome_recebedor = item.nome_recebedor || existing.nome_recebedor;
            existing.taxa_entrega = item.taxa_entrega || existing.taxa_entrega;
            existing.observacoes = item.observacoes || existing.observacoes;
            existing.foto_comprovante = item.foto_comprovante || existing.foto_comprovante;
          }
          
          // 3. Se qualquer registro do grupo estiver marcado como pago, o grupo todo é considerado pago
          if (itemPaid) {
            existing.pago = true;
          }

          // 4. Se qualquer registro do grupo estiver marcado como arquivado, o grupo todo é considerado arquivado
          if (itemArchived) {
            existing.arquivado = true;
          }
        }
      }
    });

    map.forEach(value => grouped.push(value));
    return grouped;
  }, [deliveries]);

  // Motoboy's pending deliveries
  const motoboyPending = useMemo(() => {
    return groupedDeliveries.filter(d => 
      d.motoboy_id === user?.id && 
      d.status !== "entregue" && 
      d.status !== "cancelada"
    );
  }, [groupedDeliveries, user]);

  // Motoboy's completed deliveries (filtered by time and limited to 10 records for speed)
  const motoboyCompleted = useMemo(() => {
    let completed = groupedDeliveries.filter(d => 
      d.motoboy_id === user?.id && 
      d.status === "entregue"
    );

    // Filtro de tempo
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    completed = completed.filter(d => {
      const dateStr = d.data_entrega || d.data_solicitacao || d.created;
      if (!dateStr) return true; // fallback
      const dDate = new Date(dateStr);

      if (motoboyTimeFilter === "hoje") {
        return dDate >= startOfToday;
      } else if (motoboyTimeFilter === "3dias") {
        const threeDaysAgo = new Date(startOfToday.getTime() - 3 * 24 * 60 * 60 * 1000);
        return dDate >= threeDaysAgo;
      } else if (motoboyTimeFilter === "7dias") {
        const sevenDaysAgo = new Date(startOfToday.getTime() - 7 * 24 * 60 * 60 * 1000);
        return dDate >= sevenDaysAgo;
      }
      return true; // "todos"
    });

    // Ordenar por data mais recente
    completed.sort((a, b) => {
      const dateA = new Date(a.data_entrega || a.data_solicitacao || a.created || 0);
      const dateB = new Date(b.data_entrega || b.data_solicitacao || b.created || 0);
      return dateB - dateA;
    });

    return completed.slice(0, 10);
  }, [groupedDeliveries, user, motoboyTimeFilter]);

  // Filtered versions based on motoboySearchTerm
  const filteredMotoboyPending = useMemo(() => {
    return motoboyPending.filter(d => {
      if (!motoboySearchTerm.trim()) return true;
      const term = motoboySearchTerm.toLowerCase();
      const addr = d.endereco ? `${d.endereco.logradouro || ""} ${d.endereco.numero || ""} ${d.endereco.bairro || ""} ${d.endereco.cidade || ""}`.toLowerCase() : "";
      return (d.numero_pedido || "").toLowerCase().includes(term) ||
             (d.cliente_nome || "").toLowerCase().includes(term) ||
             addr.includes(term);
    });
  }, [motoboyPending, motoboySearchTerm]);

  const filteredMotoboyCompleted = useMemo(() => {
    return motoboyCompleted.filter(d => {
      if (!motoboySearchTerm.trim()) return true;
      const term = motoboySearchTerm.toLowerCase();
      const addr = d.endereco ? `${d.endereco.logradouro || ""} ${d.endereco.numero || ""} ${d.endereco.bairro || ""} ${d.endereco.cidade || ""}`.toLowerCase() : "";
      return (d.numero_pedido || "").toLowerCase().includes(term) ||
             (d.cliente_nome || "").toLowerCase().includes(term) ||
             addr.includes(term);
    });
  }, [motoboyCompleted, motoboySearchTerm]);

  // Admin list (active - not archived)
  const adminFilteredDeliveries = useMemo(() => {
    return groupedDeliveries.filter(d => {
      if (d.arquivado === true || d.arquivado === "true") return false;
      const matchSearch = (d.numero_pedido || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (d.cliente_nome || "").toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = statusFilter === "all" ? true : d.status === statusFilter;
      const matchBoy = selectedMotoboyId === "all" ? true : d.motoboy_id === selectedMotoboyId;
      return matchSearch && matchStatus && matchBoy;
    });
  }, [groupedDeliveries, searchTerm, statusFilter, selectedMotoboyId]);

  // Admin list (archived)
  const adminArchivedDeliveries = useMemo(() => {
    return groupedDeliveries.filter(d => {
      if (d.arquivado !== true && d.arquivado !== "true") return false;
      const matchSearch = (d.numero_pedido || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (d.cliente_nome || "").toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = statusFilter === "all" ? true : d.status === statusFilter;
      const matchBoy = selectedMotoboyId === "all" ? true : d.motoboy_id === selectedMotoboyId;
      return matchSearch && matchStatus && matchBoy;
    });
  }, [groupedDeliveries, searchTerm, statusFilter, selectedMotoboyId]);

  // Account statement and transaction ledger
  const motoboyLedger = useMemo(() => {
    if (!statementMotoboyId) return { transactions: [], balance: 0 };

    // 1. Deliveries completed
    const delivs = groupedDeliveries
      .filter(d => d.motoboy_id === statementMotoboyId && d.status === "entregue")
      .map(d => ({
        id: `delivery-${d.id}`,
        date: d.data_entrega || d.updated || d.created,
        type: "delivery",
        description: `Entrega Pedido #${d.numero_pedido}`,
        amount: Number(d.taxa_entrega) || 15,
        details: d
      }));

    // 2. Payments registered
    const pays = payments
      .filter(p => p.motoboy_id === statementMotoboyId)
      .map(p => ({
        id: `payment-${p.id}`,
        date: p.data_pagamento || p.created,
        type: "payment",
        description: `Pagamento / Acerto`,
        amount: -(Number(p.valor_pago) || 0),
        details: p
      }));

    // Combine and sort chronologically (oldest to newest) to calculate running balance
    const combined = [...delivs, ...pays].sort((a, b) => new Date(a.date) - new Date(b.date));

    let runningBalance = 0;
    const transactionsWithBalance = combined.map(tx => {
      runningBalance += tx.amount;
      return {
        ...tx,
        runningBalance
      };
    });

    // Sort newest to oldest for display, but keep the correct running balance
    const displayTransactions = [...transactionsWithBalance].reverse();

    return {
      transactions: displayTransactions,
      balance: runningBalance
    };
  }, [groupedDeliveries, payments, statementMotoboyId]);

  const lastPaymentDate = useMemo(() => {
    const sortedPayments = [...payments]
      .filter(p => p.motoboy_id === statementMotoboyId)
      .sort((a, b) => new Date(b.data_pagamento || b.created) - new Date(a.data_pagamento || a.created));
    return sortedPayments.length > 0 ? sortedPayments[0].data_pagamento || sortedPayments[0].created : null;
  }, [payments, statementMotoboyId]);

  const deliveriesSinceLastPayment = useMemo(() => {
    const list = groupedDeliveries.filter(d => d.motoboy_id === statementMotoboyId && d.status === "entregue");
    if (!lastPaymentDate) return list;
    return list.filter(d => new Date(d.data_entrega || d.created) > new Date(lastPaymentDate));
  }, [groupedDeliveries, statementMotoboyId, lastPaymentDate]);

  // Keep compatibility with existing variable references in JSX where possible
  const statementUnpaidSum = motoboyLedger.balance;
  const statementUnpaidList = deliveriesSinceLastPayment;
  const statementHistoryPayments = useMemo(() => {
    return payments.filter(p => p.motoboy_id === statementMotoboyId);
  }, [payments, statementMotoboyId]);

  // Dashboard calculations
  const dashboardStats = useMemo(() => {
    const done = groupedDeliveries.filter(d => d.status === "entregue");
    const totalTax = done.reduce((sum, d) => sum + (Number(d.taxa_entrega) || 0), 0);
    const avgTax = done.length > 0 ? (totalTax / done.length) : 0;
    
    // Group by neighborhood
    const neighborhoodCounts = {};
    done.forEach(d => {
      const neighborhood = d.endereco?.bairro || "Desconhecido";
      neighborhoodCounts[neighborhood] = (neighborhoodCounts[neighborhood] || 0) + 1;
    });

    const sortedNeighborhoods = Object.entries(neighborhoodCounts)
      .sort((a,b) => b[1] - a[1])
      .slice(0, 5);

    return {
      totalRealizadas: done.length,
      totalTaxas: totalTax,
      taxaMedia: Math.round(avgTax * 100) / 100,
      bairrosComuns: sortedNeighborhoods
    };
  }, [groupedDeliveries]);

  const duplicateDiagnostic = useMemo(() => {
    if (!isAdmin) return null;
    const externos = allUsers.filter(u => 
      (u.name && u.name.trim().toUpperCase() === "MOTOBOY EXTERNO") || 
      (u.username && u.username.toLowerCase().startsWith("externo"))
    );
    if (externos.length <= 1) return null;

    return externos.map(u => {
      const count = deliveries.filter(d => d.motoboy_id === u.id).length;
      return {
        id: u.id,
        username: u.username,
        name: u.name,
        deliveriesCount: count
      };
    });
  }, [allUsers, deliveries, isAdmin]);

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-slate-50 text-slate-500">
        <RefreshCw className="animate-spin mb-2" size={32} />
        <span className="text-xs font-semibold">Carregando painel do motoboy...</span>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50">
      
      {/* ----------------- MOTOBOY VIEW ----------------- */}
      {isMotoboyRole && (
        <div className="flex-1 flex flex-col overflow-hidden w-full">
          {/* Mobile sub-tabs */}
          <div className="flex px-4 pt-3 border-b border-slate-200 bg-white gap-2 shrink-0 overflow-x-auto [&::-webkit-scrollbar]:hidden">
            <button
              onClick={() => setMotoboyActiveTab("pendentes")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
                motoboyActiveTab === "pendentes" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"
              }`}
            >
              <Clock size={15} /> Pendentes ({motoboyPending.length})
            </button>
            <button
              onClick={() => setMotoboyActiveTab("realizadas")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
                motoboyActiveTab === "realizadas" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"
              }`}
            >
              <CheckCircle size={15} /> Realizadas ({motoboyCompleted.length})
            </button>
            <button
              onClick={() => setMotoboyActiveTab("conta_corrente")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
                motoboyActiveTab === "conta_corrente" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"
              }`}
            >
              <DollarSign size={15} /> Extrato (R$ {statementUnpaidSum.toFixed(2)})
            </button>
          </div>

          {/* List Area */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4 max-w-md mx-auto w-full">
            
            {/* Search Input for Motoboy */}
            {(motoboyActiveTab === "pendentes" || motoboyActiveTab === "realizadas") && (
              <div className="relative shrink-0 w-full mb-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  placeholder="Pesquisar pedido, cliente ou endereço..."
                  value={motoboySearchTerm}
                  onChange={(e) => setMotoboySearchTerm(e.target.value)}
                  className="w-full pl-9 pr-8 py-2.5 border border-slate-300 rounded-xl text-xs outline-none focus:ring-1 focus:ring-blue-500 font-bold bg-white shadow-sm"
                />
                {motoboySearchTerm && (
                  <button
                    onClick={() => setMotoboySearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-450 hover:text-slate-600 font-bold text-xs"
                  >
                    ✕
                  </button>
                )}
              </div>
            )}

            {motoboyActiveTab === "realizadas" && (
              <div className="flex gap-1.5 shrink-0 overflow-x-auto pb-1 mt-2 mb-2 [&::-webkit-scrollbar]:hidden justify-center">
                {[
                  { id: "hoje", label: "Hoje" },
                  { id: "3dias", label: "3 Dias" },
                  { id: "7dias", label: "7 Dias" },
                  { id: "todos", label: "Tudo" }
                ].map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => setMotoboyTimeFilter(filter.id)}
                    className={`px-3.5 py-1.5 rounded-full text-[10px] font-extrabold uppercase transition-all shadow-sm ${
                      motoboyTimeFilter === filter.id
                        ? "bg-blue-600 text-white"
                        : "bg-slate-100 text-slate-650 hover:bg-slate-200"
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            )}

            {motoboyActiveTab === "pendentes" ? (
              <>
                {/* Rota Otimizada Banner */}
                {filteredMotoboyPending.length >= 2 && (
                  <a
                    href={getOptimizedRouteLink(filteredMotoboyPending)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-slate-900 hover:bg-slate-950 text-white rounded-2xl p-4 shadow-md flex items-center justify-between transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-blue-600 rounded-xl flex items-center justify-center">
                        <Navigation size={20} className="animate-pulse" />
                      </div>
                      <div className="text-left">
                        <span className="font-extrabold text-sm block">Traçar Rota Otimizada</span>
                        <span className="text-[10px] text-slate-400 font-semibold">Navegar com {filteredMotoboyPending.length} paradas</span>
                      </div>
                    </div>
                    <span className="text-xs font-bold bg-white/10 px-2.5 py-1 rounded-full uppercase tracking-wider">Ir</span>
                  </a>
                )}

                {/* List items */}
                {filteredMotoboyPending.length > 0 ? (
                  filteredMotoboyPending.map(del => {
                    const isSaiu = del.status === "saiu_para_entrega";

                    return (
                      <div key={del.id} className={`bg-white rounded-2xl border p-4 shadow-sm flex flex-col gap-3 relative border-l-4 ${
                        isSaiu ? "border-l-blue-500 ring-1 ring-blue-100" : "border-l-slate-300"
                      }`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="font-extrabold text-sm text-blue-600">#{del.numero_pedido}</span>
                              {(!!del.direta_urgente || (del.observacoes && /\[(?:DIRETA\/)?URGENTE\]/i.test(del.observacoes))) && (
                                <span className="text-[8px] font-extrabold text-white bg-red-500 px-1 py-0.5 rounded-full uppercase tracking-wider animate-pulse inline-block">
                                  ⚡ DIRETA
                                </span>
                              )}
                            </div>
                            <span className="font-bold text-xs text-slate-800">{del.cliente_nome}</span>
                          </div>
                          
                          <div className="flex flex-col items-end gap-1">
                            <span className={`px-2 py-0.5 rounded text-[8px] font-extrabold uppercase ${
                              isSaiu ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-600"
                            }`}>
                              {del.status === "saiu_para_entrega" ? "A Caminho" : "Pendente"}
                            </span>
                            {del.distancia_km > 0 && (
                              <span className="text-[10px] text-slate-500 font-bold bg-slate-100 px-1.5 py-0.5 rounded">
                                📏 {del.distancia_km} KM
                              </span>
                            )}
                            {del.valor_sugerido > 0 && (
                              <span className="text-[10px] text-blue-600 font-bold bg-blue-50 px-1.5 py-0.5 rounded">
                                💰 Sug: R$ {del.valor_sugerido.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Endereco Block */}
                        <div className="flex flex-col gap-2">
                          {del.endereco_retirada && formatAddress(del.endereco_retirada) ? (
                            <div className="bg-amber-50/60 p-3 rounded-xl border border-amber-200/60 text-xs font-semibold text-slate-700">
                              <p className="font-extrabold text-amber-800 text-[9px] uppercase tracking-wider mb-1 flex items-center gap-1">
                                🏠 Endereço de Retirada (Origem)
                              </p>
                              <p>{del.endereco_retirada.logradouro}, {del.endereco_retirada.numero}</p>
                              {del.endereco_retirada.complemento && <p className="text-[10px] opacity-85">Compl: {del.endereco_retirada.complemento}</p>}
                              <p className="text-[10px] opacity-85">{del.endereco_retirada.bairro} • {del.endereco_retirada.cidade}-{del.endereco_retirada.estado}</p>
                            </div>
                          ) : null}

                          <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/50 text-xs font-semibold text-slate-600">
                            <p className="font-extrabold text-blue-700 text-[9px] uppercase tracking-wider mb-1 flex items-center gap-1">
                              📍 Endereço de Entrega (Destino)
                            </p>
                            <p>{del.endereco?.logradouro}, {del.endereco?.numero}</p>
                            {del.endereco?.complemento && <p className="text-[10px] opacity-80">Compl: {del.endereco.complemento}</p>}
                            <p className="text-[10px] opacity-80">{del.endereco?.bairro} • {del.endereco?.cidade}-{del.endereco?.estado}</p>
                          </div>
                        </div>

                        {/* Buttons Grid */}
                        <div className="flex flex-col gap-2 mt-1">
                          <div className="grid grid-cols-2 gap-2">
                            {del.endereco_retirada && formatAddress(del.endereco_retirada) ? (
                              <a
                                href={getMapsLink(del.endereco_retirada)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 font-bold p-2.5 rounded-xl text-xs flex items-center justify-center gap-1 shadow-sm transition-all"
                              >
                                <MapPin size={12} className="text-amber-500" /> GPS Retirada
                              </a>
                            ) : null}

                            <a
                              href={getMapsLink(del.endereco)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`bg-slate-100 hover:bg-slate-250 border border-slate-250 text-slate-700 font-bold p-2.5 rounded-xl text-xs flex items-center justify-center gap-1 shadow-sm transition-all ${
                                del.endereco_retirada && formatAddress(del.endereco_retirada) ? "" : "col-span-2"
                              }`}
                            >
                              <MapPin size={12} className="text-red-500" /> GPS {del.endereco_retirada && formatAddress(del.endereco_retirada) ? "Entrega" : ""}
                            </a>
                          </div>

                          {!isSaiu ? (
                            <button
                              onClick={() => handleStartDelivery(del)}
                              className="bg-blue-600 hover:bg-blue-700 text-white font-bold p-2.5 rounded-xl text-xs flex items-center justify-center gap-1 shadow-sm transition-all"
                            >
                              <Bike size={14} /> Entregar
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                  setActiveDelivery(del);
                                  setIsConfirmModalOpen(true);
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold p-2.5 rounded-xl text-xs flex items-center justify-center gap-1 shadow-sm transition-all"
                            >
                              <CheckCircle size={14} /> Finalizar
                            </button>
                          )}
                        </div>

                        {isSaiu && (
                          <button
                            onClick={() => handlePostponeDelivery(del)}
                            className="text-center text-[10px] font-bold text-slate-400 hover:text-slate-600 pt-1 border-t border-slate-100"
                          >
                            Não foi possível entregar (Adiar)
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="h-64 flex flex-col items-center justify-center text-slate-400 text-center">
                    <Bike size={48} className="mb-2 text-slate-300" />
                    <span className="text-xs font-bold">Nenhuma entrega pendente correspondente.</span>
                  </div>
                )}
              </>
            ) : motoboyActiveTab === "realizadas" ? (
              /* REALIZADAS */
              <>
                {filteredMotoboyCompleted.length > 0 ? (
                  filteredMotoboyCompleted.map(del => (
                    <div key={del.id} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-col gap-2 relative">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1.5">
                          <span className="font-extrabold text-xs text-slate-700">Pedido #{del.numero_pedido}</span>
                          {(!!del.direta_urgente || (del.observacoes && /\[(?:DIRETA\/)?URGENTE\]/i.test(del.observacoes))) && (
                            <span className="text-[8px] font-extrabold text-white bg-red-500 px-1 py-0.5 rounded-full uppercase tracking-wider animate-pulse inline-block">
                              ⚡ DIRETA
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {del.distancia_km > 0 && (
                            <span className="text-[9px] text-slate-500 font-bold bg-slate-100 px-1 py-0.5 rounded">
                              {del.distancia_km} KM
                            </span>
                          )}
                          <span className="text-xs font-extrabold text-emerald-600 font-mono">R$ {del.taxa_entrega || 15}</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 font-semibold">{del.cliente_nome}</p>
                      {del.observacoes && (
                        <p className="text-[10px] text-slate-400 italic mt-1 bg-slate-50 p-2 border border-slate-100 rounded">
                          "{del.observacoes}"
                        </p>
                      )}
                      <div className="flex justify-between text-[10px] font-semibold text-slate-400 border-t border-slate-100 pt-2">
                        <span>Data: <strong className="text-slate-500 font-mono">{new Date(del.data_entrega).toLocaleDateString("pt-BR")}</strong></span>
                        <span>Recebedor: <strong className="text-slate-500">{del.nome_recebedor}</strong></span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="h-64 flex flex-col items-center justify-center text-slate-400 text-center">
                    <CheckCircle size={48} className="mb-2 text-slate-300" />
                    <span className="text-xs font-bold">Nenhuma entrega concluída correspondente.</span>
                  </div>
                )}
              </>
            ) : (
              /* CONTA CORRENTE (EXTRATO) */
              <div className="space-y-4 text-xs">
                {/* Balance Card */}
                <div className="bg-slate-900 border border-slate-800 text-white rounded-2xl p-5 shadow-md flex flex-col justify-center items-center text-center">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Saldo Atual a Receber</span>
                  <span className="text-2xl font-black font-mono text-emerald-400">R$ {motoboyLedger.balance.toFixed(2)}</span>
                </div>

                {/* List of transactions */}
                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-col">
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3 shrink-0 flex items-center gap-1.5">
                    <History size={14} className="text-slate-400" /> Extrato de Movimentações
                  </h4>
                  
                  <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-1">
                    {motoboyLedger.transactions.length > 0 ? (
                      motoboyLedger.transactions.map(tx => {
                        const isDelivery = tx.type === "delivery";
                        return (
                          <div key={tx.id} className={`p-3 rounded-xl border flex flex-col gap-1.5 ${
                            isDelivery ? "border-slate-150 bg-slate-50/50" : "border-emerald-100 bg-emerald-50/20"
                          }`}>
                            <div className="flex justify-between items-center text-xs">
                              <span className="font-extrabold text-slate-700">{tx.description}</span>
                              <span className={`font-mono font-bold ${
                                isDelivery ? "text-blue-600" : "text-emerald-600"
                              }`}>
                                {isDelivery ? "+" : "-"} R$ {Math.abs(tx.amount).toFixed(2)}
                              </span>
                            </div>
                            
                            {tx.details.observacoes && (
                              <p className="text-[10px] text-slate-500 italic bg-white/80 p-1.5 border border-slate-100 rounded">
                                "{tx.details.observacoes}"
                              </p>
                            )}

                            {isDelivery && tx.details.cliente_nome && (
                              <div className="text-[10px] text-slate-500">
                                Cliente: <span className="font-semibold">{tx.details.cliente_nome}</span>
                              </div>
                            )}

                            <div className="flex justify-between items-center text-[9px] text-slate-400 font-bold border-t border-slate-100/60 pt-1.5 mt-1">
                              <span className="font-mono">
                                {new Date(tx.date).toLocaleDateString("pt-BR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit"
                                })}
                              </span>
                              <span>
                                Saldo: <strong className="text-slate-600 font-mono">R$ {tx.runningBalance.toFixed(2)}</strong>
                              </span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-8 text-center text-slate-400 font-bold">
                        Nenhuma movimentação registrada.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ----------------- ADMIN GESTOR VIEW ----------------- */}
      {isAdmin && (
        <div className="flex-1 flex flex-col overflow-hidden w-full">
          {/* Admin subtabs */}
          <div className="flex px-6 pt-4 border-b border-slate-200 bg-white gap-2 shrink-0 overflow-x-auto [&::-webkit-scrollbar]:hidden">
            <button
              onClick={() => setAdminActiveTab("entregas")}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
                adminActiveTab === "entregas" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"
              }`}
            >
              <Bike size={15} /> Painel de Entregas
            </button>
            <button
              onClick={() => setAdminActiveTab("conta_corrente")}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
                adminActiveTab === "conta_corrente" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"
              }`}
            >
              <DollarSign size={15} /> Conta-Corrente Motoboy
            </button>
            <button
              onClick={() => setAdminActiveTab("dashboard")}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
                adminActiveTab === "dashboard" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"
              }`}
            >
              <BarChart2 size={15} /> Dashboard Motoboy
            </button>
            <button
              onClick={() => setAdminActiveTab("arquivo")}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
                adminActiveTab === "arquivo" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"
              }`}
            >
              <Archive size={15} /> Arquivo
            </button>
            <button
              onClick={() => setAdminActiveTab("configuracoes")}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap ${
                adminActiveTab === "configuracoes" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"
              }`}
            >
              <Settings size={15} /> Configurações de Tarifas
            </button>
          </div>

          {duplicateDiagnostic && (
            <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-2xl shadow-sm text-xs text-slate-700 flex flex-col gap-2 shrink-0">
              <div className="flex items-center gap-2 text-amber-800 font-bold">
                <ShieldAlert size={16} />
                <span>Duplicidade de Usuário Virtual Detectada!</span>
              </div>
              <p className="text-slate-600 font-medium">
                Identificamos contas duplicadas do <strong>MOTOBOY EXTERNO</strong> no banco de dados. 
                Veja abaixo a contagem de entregas associadas a cada uma para saber qual delas pode remover com segurança:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1 font-bold">
                {duplicateDiagnostic.map(ext => (
                  <div key={ext.id} className={`p-3 rounded-xl border flex flex-col gap-1 ${
                    ext.deliveriesCount === 0 ? "bg-emerald-50/50 border-emerald-250 text-emerald-900" : "bg-red-50/50 border-red-200 text-red-950"
                  }`}>
                    <span className="text-[10px] uppercase tracking-wider opacity-75">Usuário</span>
                    <span className="text-sm">@{ext.username}</span>
                    <span className="text-[10px] opacity-75">ID: {ext.id}</span>
                    <div className="mt-2 flex items-center justify-between text-xs font-black">
                      <span>{ext.deliveriesCount} entregas associadas</span>
                      {ext.deliveriesCount === 0 ? (
                        <span className="bg-emerald-600 text-white px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-extrabold">Seguro remover</span>
                      ) : (
                        <span className="bg-red-650 text-white px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-extrabold">Não remover</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex-1 overflow-hidden relative">
            
            {/* SUB-TAB 1: PAINEL DE ENTREGAS */}
            {adminActiveTab === "entregas" && (
              <div className="h-full flex flex-col overflow-hidden p-6 space-y-4">
                {/* Filters */}
                <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex flex-col md:flex-row gap-3 items-center justify-between shrink-0">
                  <div className="flex flex-wrap gap-2 items-center w-full md:w-auto">
                    <input
                      type="text"
                      placeholder="Buscar pedido ou cliente..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:border-blue-500 w-full sm:w-48"
                    />

                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-2.5 py-1.5 border border-slate-250 rounded-lg text-xs font-bold text-slate-700 bg-white"
                    >
                      <option value="all">Status: Todos</option>
                      <option value="pendente">Pendente</option>
                      <option value="saiu_para_entrega">Saiu para Entrega</option>
                      <option value="entregue">Entregue</option>
                      <option value="adiada">Adiada</option>
                    </select>

                    <select
                      value={selectedMotoboyId}
                      onChange={(e) => setSelectedMotoboyId(e.target.value)}
                      className="px-2.5 py-1.5 border border-slate-250 rounded-lg text-xs font-bold text-slate-700 bg-white"
                    >
                      <option value="all">Motoboy: Todos</option>
                      {motoboys.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={openManualInsertModal}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2.5 rounded-xl text-xs flex items-center gap-1.5 shadow-sm transition-all shrink-0 w-full md:w-auto justify-center"
                  >
                    <Plus size={14} /> Nova Entrega Manual
                  </button>
                </div>

                {/* Table */}
                <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-0">
                  <div className="flex-1 overflow-auto custom-scrollbar">
                    <table className="w-full border-collapse text-left text-xs">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 font-bold border-b border-slate-200">
                          <th className="p-3.5">Pedido</th>
                          <th className="p-3.5">Cliente</th>
                          <th className="p-3.5">Endereço</th>
                          <th className="p-3.5">Status</th>
                          <th className="p-3.5">Motoboy Atribuído</th>
                          <th className="p-3.5 text-right">Taxa Sugerida</th>
                          <th className="p-3.5 text-right">Taxa Final</th>
                          <th className="p-3.5 text-center">Comprovante</th>
                          <th className="p-3.5 text-center">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                        {adminFilteredDeliveries.map(del => {
                          const matchingBoy = motoboys.find(m => m.id === del.motoboy_id);

                          return (
                            <tr key={del.id} className="hover:bg-slate-50/50">
                              <td className="p-3.5">
                                <div className="flex items-center gap-1.5 mb-1">
                                  <span className="font-bold text-blue-600">#{del.numero_pedido}</span>
                                  {(!!del.direta_urgente || (del.observacoes && /\[(?:DIRETA\/)?URGENTE\]/i.test(del.observacoes))) && (
                                    <span className="text-[8px] font-extrabold text-white bg-red-500 px-1.5 py-0.5 rounded-full uppercase tracking-wider animate-pulse inline-block">
                                      ⚡ DIRETA
                                    </span>
                                  )}
                                </div>
                                {del.solicitante_id && (
                                  <span className="text-[9px] text-slate-400 font-medium block">
                                    Solicitado por: {allUsers.find(u => u.id === del.solicitante_id)?.name || allUsers.find(u => u.id === del.solicitante_id)?.username || "Sistema"}
                                  </span>
                                )}
                              </td>
                              <td className="p-3.5">{del.cliente_nome}</td>
                              <td className="p-3.5 max-w-xs">
                                <div className="space-y-1">
                                  {del.endereco_retirada && formatAddress(del.endereco_retirada) ? (
                                    <div className="text-[10px] font-medium text-slate-500 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5" title={formatAddress(del.endereco_retirada)}>
                                      <span className="font-bold text-amber-700">🏠 Retirada:</span> {formatAddress(del.endereco_retirada)}
                                    </div>
                                  ) : null}
                                  <div className="text-[10px]" title={formatAddress(del.endereco)}>
                                    <span className="font-bold text-blue-700">📍 Entrega:</span> {formatAddress(del.endereco)}
                                  </div>
                                </div>
                              </td>
                              <td className="p-3.5">
                                <span className={`px-2 py-0.5 rounded text-[8px] font-extrabold uppercase ${
                                  del.status === "entregue"
                                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                    : del.status === "saiu_para_entrega"
                                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                                    : del.status === "adiada"
                                    ? "bg-amber-50 text-amber-700 border border-amber-200"
                                    : "bg-slate-100 text-slate-600"
                                }`}>
                                  {del.status}
                                </span>
                              </td>
                              <td className="p-3.5">
                                <div className="flex flex-col gap-0.5">
                                  <button
                                    onClick={() => handleAssignMotoboy(del)}
                                    className="text-xs text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1 font-bold text-left"
                                  >
                                    {matchingBoy ? matchingBoy.name : "Atribuir..."}
                                  </button>
                                  {matchingBoy && matchingBoy.name === "MOTOBOY EXTERNO" && (
                                    <button
                                      onClick={() => handleOpenExternalEdit(del)}
                                      className="text-[10px] text-emerald-600 hover:text-emerald-700 hover:underline font-bold text-left flex items-center gap-0.5"
                                    >
                                      Gerenciar...
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="p-3.5 text-right font-mono text-slate-800">
                                {del.valor_sugerido > 0 ? (
                                  <div>
                                    <div className="font-bold text-blue-600">R$ {del.valor_sugerido.toFixed(2)}</div>
                                    {del.distancia_km > 0 && (
                                      <div className="text-[9px] text-slate-500 font-bold bg-slate-100 px-1 py-0.5 rounded inline-block">
                                        {del.distancia_km} KM
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="p-3.5 text-right font-mono text-slate-800">
                                {del.status === "entregue" ? (
                                  <span className="font-bold text-slate-800">R$ {del.taxa_entrega || 15}</span>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="p-3.5 text-center">
                                {del.foto_comprovante ? (
                                  <a
                                    href={pb.files.getURL(del, del.foto_comprovante)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 text-slate-400 hover:text-blue-600 inline-block bg-slate-100 rounded-lg hover:shadow-sm"
                                    title="Ver Comprovante"
                                  >
                                    <Eye size={14} />
                                  </a>
                                ) : "—"}
                              </td>
                              <td className="p-3.5">
                                <div className="flex items-center justify-center gap-1.5">
                                  <button
                                    onClick={() => handleCalculateSuggestedPriceForDelivery(del)}
                                    className="p-1.5 text-amber-600 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-lg transition-all"
                                    title="Calcular/Estimar Rota e Valor"
                                  >
                                    <RefreshCw size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleOpenGeneralEditModal(del)}
                                    className="p-1.5 text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all"
                                    title="Editar Entrega"
                                  >
                                    <Edit size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleArchiveDelivery(del, true)}
                                    className="p-1.5 text-amber-600 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-lg transition-all"
                                    title="Arquivar Entrega"
                                  >
                                    <Archive size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteDelivery(del)}
                                    className="p-1.5 text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-all"
                                    title="Apagar Entrega"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* SUB-TAB 2: CONTA-CORRENTE MOTOBOY */}
            {adminActiveTab === "conta_corrente" && (
              <div className="h-full flex flex-col p-6 gap-6 overflow-hidden min-h-0">
                {/* Ledger & selector */}
                <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col min-h-0">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-100 pb-4 shrink-0">
                    <div>
                      <h3 className="font-extrabold text-slate-800 text-sm">Extrato do Conta-Corrente</h3>
                      <p className="text-[10px] text-slate-400">Selecione o motoboy para auditar e dar baixa</p>
                    </div>

                    <select
                      value={statementMotoboyId}
                      onChange={(e) => setStatementMotoboyId(e.target.value)}
                      className="px-3 py-1.5 border border-slate-300 rounded-xl focus:ring-1 focus:ring-blue-500 font-bold text-xs bg-white cursor-pointer"
                    >
                      {motoboys.filter(m => m.name !== "MOTOBOY EXTERNO").map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Extrato Metrics */}
                  <div className="grid grid-cols-2 gap-4 my-5 shrink-0">
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-center">
                      <span className="text-[9px] font-bold text-red-500 uppercase tracking-wider block mb-1">Saldo Atual Devedor</span>
                      <span className="text-2xl font-black font-mono text-red-900">R$ {motoboyLedger.balance.toFixed(2)}</span>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex flex-col justify-center items-center text-center">
                      <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Entregas no Período Atual</span>
                      <span className="text-2xl font-black font-mono text-slate-800">{deliveriesSinceLastPayment.length}</span>
                    </div>
                  </div>

                  {/* Transaction Ledger Table */}
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 shrink-0">Extrato de Movimentações</h4>
                  
                  <div className="flex-1 overflow-auto custom-scrollbar border border-slate-150 rounded-xl">
                    <table className="w-full border-collapse text-left text-xs">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 font-bold border-b border-slate-200">
                          <th className="p-3">Data/Hora</th>
                          <th className="p-3">Tipo</th>
                          <th className="p-3">Descrição / Detalhes</th>
                          <th className="p-3 text-right">Valor</th>
                          <th className="p-3 text-right">Saldo do Momento</th>
                          <th className="p-3 text-center">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-150 font-semibold text-slate-600">
                        {motoboyLedger.transactions.length > 0 ? (
                          motoboyLedger.transactions.map(tx => {
                            const isDelivery = tx.type === "delivery";
                            return (
                              <tr key={tx.id} className="hover:bg-slate-50/50">
                                <td className="p-3 font-mono">
                                  {new Date(tx.date).toLocaleDateString("pt-BR", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit"
                                  })}
                                </td>
                                <td className="p-3">
                                  <span className={`px-2 py-0.5 rounded text-[8px] font-extrabold uppercase ${
                                    isDelivery 
                                      ? "bg-blue-50 text-blue-700 border border-blue-200" 
                                      : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  }`}>
                                    {isDelivery ? "Entrega" : "Pagamento"}
                                  </span>
                                </td>
                                <td className="p-3">
                                  <div className="text-slate-800">{tx.description}</div>
                                  {tx.details.observacoes && (
                                    <div className="text-[10px] text-slate-400 font-normal italic mt-0.5">
                                      "{tx.details.observacoes}"
                                    </div>
                                  )}
                                  {isDelivery && tx.details.cliente_nome && (
                                    <div className="text-[10px] text-slate-400 font-normal">
                                      Cliente: {tx.details.cliente_nome}
                                    </div>
                                  )}
                                </td>
                                <td className={`p-3 text-right font-mono font-bold ${
                                  isDelivery ? "text-blue-600" : "text-emerald-600"
                                }`}>
                                  {isDelivery ? "+" : "-"} R$ {Math.abs(tx.amount).toFixed(2)}
                                </td>
                                <td className="p-3 text-right font-mono font-bold text-slate-700">
                                  R$ {tx.runningBalance.toFixed(2)}
                                </td>
                                <td className="p-3 text-center">
                                  {tx.type === "payment" && user?.role === "master" && (
                                    <button
                                      onClick={() => handleDeletePayment(tx.details.id)}
                                      className="p-1 text-red-500 hover:text-red-700 inline-block bg-red-50 hover:bg-red-100 rounded-lg transition-all"
                                      title="Apagar Pagamento"
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={6} className="p-8 text-center text-slate-400 font-bold">
                              Nenhuma movimentação registrada para este motoboy.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {user?.role === "master" && (
                    <div className="mt-4 pt-3 border-t border-slate-150 flex justify-end shrink-0">
                      <button
                        onClick={() => setIsPaymentModalOpen(true)}
                        disabled={motoboyLedger.balance <= 0}
                        className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold px-4 py-2.5 rounded-xl text-xs flex items-center gap-1 shadow-sm transition-all"
                      >
                        <DollarSign size={14} /> Registrar Pagamento
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* SUB-TAB 3: DASHBOARD METRICS */}
            {adminActiveTab === "dashboard" && (
              <div className="h-full overflow-y-auto custom-scrollbar p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Cards */}
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 text-white shadow-sm text-center">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Total de Entregas Realizadas</span>
                    <span className="text-3xl font-extrabold font-mono text-amber-400">{dashboardStats.totalRealizadas}</span>
                  </div>

                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 text-white shadow-sm text-center">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Total Movimentado Taxas</span>
                    <span className="text-3xl font-extrabold font-mono text-amber-400">R$ {dashboardStats.totalTaxas.toFixed(2)}</span>
                  </div>

                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 text-white shadow-sm text-center">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Taxa Média por Entrega</span>
                    <span className="text-3xl font-extrabold font-mono text-amber-400">R$ {dashboardStats.taxaMedia.toFixed(2)}</span>
                  </div>
                </div>

                {/* Regions / Neighborhood list */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 max-w-lg">
                  <h3 className="font-extrabold text-slate-700 text-sm mb-4 border-b border-slate-100 pb-3">
                    Regiões e Bairros Mais Atendidos
                  </h3>

                  <div className="space-y-3">
                    {dashboardStats.bairrosComuns.map(([bairro, count], idx) => (
                      <div key={bairro} className="flex items-center gap-3">
                        <span className="w-6 text-center font-bold text-xs text-slate-400">#{idx + 1}</span>
                        <div className="flex-1 bg-slate-50 p-2 border border-slate-200/60 rounded-xl flex justify-between items-center text-xs font-semibold text-slate-700">
                          <span>{bairro}</span>
                          <span className="font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{count} entregas</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* SUB-TAB 4: ENTREGAS ARQUIVADAS */}
            {adminActiveTab === "arquivo" && (
              <div className="h-full flex flex-col overflow-hidden p-6 space-y-4">
                {/* Filters */}
                <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex flex-col md:flex-row gap-3 items-center justify-between shrink-0">
                  <div className="flex flex-wrap gap-2 items-center w-full md:w-auto">
                    <input
                      type="text"
                      placeholder="Buscar pedido ou cliente arquivado..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:border-blue-500 w-full sm:w-48"
                    />

                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-2.5 py-1.5 border border-slate-250 rounded-lg text-xs font-bold text-slate-700 bg-white"
                    >
                      <option value="all">Status: Todos</option>
                      <option value="pendente">Pendente</option>
                      <option value="saiu_para_entrega">Saiu para Entrega</option>
                      <option value="entregue">Entregue</option>
                      <option value="adiada">Adiada</option>
                    </select>

                    <select
                      value={selectedMotoboyId}
                      onChange={(e) => setSelectedMotoboyId(e.target.value)}
                      className="px-2.5 py-1.5 border border-slate-250 rounded-lg text-xs font-bold text-slate-700 bg-white"
                    >
                      <option value="all">Motoboy: Todos</option>
                      {motoboys.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Table */}
                <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-0">
                  <div className="flex-1 overflow-auto custom-scrollbar">
                    <table className="w-full border-collapse text-left text-xs">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 font-bold border-b border-slate-200">
                          <th className="p-3.5">Pedido</th>
                          <th className="p-3.5">Cliente</th>
                          <th className="p-3.5">Endereço</th>
                          <th className="p-3.5">Status</th>
                          <th className="p-3.5">Motoboy Atribuído</th>
                          <th className="p-3.5 text-right">Taxa Sugerida</th>
                          <th className="p-3.5 text-right">Taxa Final</th>
                          <th className="p-3.5 text-center">Comprovante</th>
                          <th className="p-3.5 text-center">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                        {adminArchivedDeliveries.map(del => {
                          const matchingBoy = motoboys.find(m => m.id === del.motoboy_id);

                          return (
                            <tr key={del.id} className="hover:bg-slate-50/50">
                              <td className="p-3.5">
                                <div className="flex items-center gap-1.5 mb-1">
                                  <span className="font-bold text-blue-600">#{del.numero_pedido}</span>
                                  {(!!del.direta_urgente || (del.observacoes && /\[(?:DIRETA\/)?URGENTE\]/i.test(del.observacoes))) && (
                                    <span className="text-[8px] font-extrabold text-white bg-red-500 px-1.5 py-0.5 rounded-full uppercase tracking-wider animate-pulse inline-block">
                                      ⚡ DIRETA
                                    </span>
                                  )}
                                </div>
                                {del.solicitante_id && (
                                  <span className="text-[9px] text-slate-400 font-medium block">
                                    Solicitado por: {allUsers.find(u => u.id === del.solicitante_id)?.name || allUsers.find(u => u.id === del.solicitante_id)?.username || "Sistema"}
                                  </span>
                                )}
                              </td>
                              <td className="p-3.5">{del.cliente_nome}</td>
                              <td className="p-3.5 max-w-xs">
                                <div className="space-y-1">
                                  {del.endereco_retirada && formatAddress(del.endereco_retirada) ? (
                                    <div className="text-[10px] font-medium text-slate-500 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5" title={formatAddress(del.endereco_retirada)}>
                                      <span className="font-bold text-amber-700">🏠 Retirada:</span> {formatAddress(del.endereco_retirada)}
                                    </div>
                                  ) : null}
                                  <div className="text-[10px]" title={formatAddress(del.endereco)}>
                                    <span className="font-bold text-blue-700">📍 Entrega:</span> {formatAddress(del.endereco)}
                                  </div>
                                </div>
                              </td>
                              <td className="p-3.5">
                                <span className={`px-2 py-0.5 rounded text-[8px] font-extrabold uppercase ${
                                  del.status === "entregue"
                                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                    : del.status === "saiu_para_entrega"
                                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                                    : del.status === "adiada"
                                    ? "bg-amber-50 text-amber-700 border border-amber-200"
                                    : "bg-slate-100 text-slate-600"
                                }`}>
                                  {del.status}
                                </span>
                              </td>
                              <td className="p-3.5">
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-xs font-semibold text-slate-700">
                                    {matchingBoy ? matchingBoy.name : "Nenhum"}
                                  </span>
                                </div>
                              </td>
                              <td className="p-3.5 text-right font-mono text-slate-800">
                                {del.valor_sugerido > 0 ? (
                                  <div>
                                    <div className="font-bold text-blue-600">R$ {del.valor_sugerido.toFixed(2)}</div>
                                    {del.distancia_km > 0 && (
                                      <div className="text-[9px] text-slate-500 font-bold bg-slate-100 px-1 py-0.5 rounded inline-block">
                                        {del.distancia_km} KM
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="p-3.5 text-right font-mono text-slate-800">
                                {del.status === "entregue" ? (
                                  <span className="font-bold text-slate-800">R$ {del.taxa_entrega || 15}</span>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="p-3.5 text-center">
                                {del.foto_comprovante ? (
                                  <a
                                    href={pb.files.getURL(del, del.foto_comprovante)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 text-slate-400 hover:text-blue-600 inline-block bg-slate-100 rounded-lg hover:shadow-sm"
                                    title="Ver Comprovante"
                                  >
                                    <Eye size={14} />
                                  </a>
                                ) : "—"}
                              </td>
                              <td className="p-3.5">
                                <div className="flex items-center justify-center gap-1.5">
                                  <button
                                    onClick={() => handleCalculateSuggestedPriceForDelivery(del)}
                                    className="p-1.5 text-amber-600 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-lg transition-all"
                                    title="Calcular/Estimar Rota e Valor"
                                  >
                                    <RefreshCw size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleOpenGeneralEditModal(del)}
                                    className="p-1.5 text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all"
                                    title="Editar Entrega"
                                  >
                                    <Edit size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleArchiveDelivery(del, false)}
                                    className="p-1.5 text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all"
                                    title="Desarquivar Entrega"
                                  >
                                    <RotateCcw size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteDelivery(del)}
                                    className="p-1.5 text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-all"
                                    title="Apagar Entrega"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {adminArchivedDeliveries.length === 0 && (
                          <tr>
                            <td colSpan={9} className="p-8 text-center text-slate-400 font-bold">
                              Nenhuma entrega arquivada.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* SUB-TAB 5: CONFIGURAÇÕES DE TARIFAS */}
            {adminActiveTab === "configuracoes" && (
              <div className="h-full flex flex-col overflow-auto p-6 space-y-6">
                <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm max-w-2xl space-y-6">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                      <Settings className="text-blue-600" size={18} /> Google Maps API Configuration
                    </h3>
                    <p className="text-xs text-slate-500 mt-2">
                      Insira sua chave de API do Google Maps para habilitar o cálculo preciso de KM via Distance Matrix.
                      Se nenhuma chave for fornecida, o sistema usará automaticamente a API gratuita do OpenStreetMap (Nominatim/OSRM).
                    </p>
                    <div className="mt-4">
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Google Maps API Key</label>
                      <div className="flex gap-2">
                        <input
                          type="password"
                          id="google_maps_api_key_input"
                          defaultValue={googleMapsKey}
                          placeholder="AIzaSy..."
                          className="flex-1 p-2 border border-slate-350 rounded-lg text-xs outline-none focus:ring-1 focus:ring-blue-500 font-mono bg-white"
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            const keyVal = document.getElementById("google_maps_api_key_input").value.trim();
                            try {
                              let configs = [];
                              try {
                                configs = await pb.collection("motoboy_configs").getFullList();
                              } catch (e) {
                                throw new Error("Coleção inacessível");
                              }
                              
                              if (configs.length > 0) {
                                await pb.collection("motoboy_configs").update(configs[0].id, { google_maps_api_key: keyVal });
                              } else {
                                await pb.collection("motoboy_configs").create({ google_maps_api_key: keyVal });
                              }
                              
                              localStorage.setItem("semprejoias_google_maps_api_key", keyVal);
                              setGoogleMapsKey(keyVal);
                              setDeliveries(p => [...p]);
                              alert("Chave de API do Google Maps salva com sucesso no banco de dados!");
                            } catch (err) {
                              console.warn("Falha ao salvar no PocketBase, usando localStorage:", err);
                              localStorage.setItem("semprejoias_google_maps_api_key", keyVal);
                              setGoogleMapsKey(keyVal);
                              setDeliveries(p => [...p]);
                              alert("Salvo localmente no navegador. (Nota: Para salvar permanentemente no banco para todos os PCs, crie a coleção 'motoboy_configs' com o campo 'google_maps_api_key' no PocketBase).");
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs transition-all shadow-sm"
                        >
                          Salvar
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const configs = await pb.collection("motoboy_configs").getFullList();
                              if (configs.length > 0) {
                                await pb.collection("motoboy_configs").update(configs[0].id, { google_maps_api_key: "" });
                              }
                            } catch (err) {
                              console.warn("Falha ao remover no PocketBase:", err);
                            }
                            localStorage.removeItem("semprejoias_google_maps_api_key");
                            setGoogleMapsKey("");
                            const keyInput = document.getElementById("google_maps_api_key_input");
                            if (keyInput) keyInput.value = "";
                            setDeliveries(p => [...p]);
                            alert("Chave de API removida!");
                          }}
                          className="px-3 py-2 bg-slate-150 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-xs transition-all"
                        >
                          Limpar
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                      <DollarSign className="text-blue-600" size={18} /> Taxa por Motoboy (Valor por KM)
                    </h3>
                    <p className="text-xs text-slate-500 mt-2">
                      Configure a taxa por quilômetro (KM) para cada motoboy. Esse valor é multiplicado pela distância da rota para gerar o valor sugerido.
                    </p>
                    <div className="mt-4 space-y-3">
                      {motoboys.filter(m => m.name !== "MOTOBOY EXTERNO").map(m => {
                        const currentRate = m.taxa_motoboy || parseFloat(localStorage.getItem(`motoboy_rate_${m.id}`)) || 0;
                        return (
                          <div key={m.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs font-semibold">
                            <span className="font-bold text-slate-700">{m.name || m.username}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-505 font-bold">R$</span>
                              <input
                                type="number"
                                step="0.01"
                                id={`motoboy_rate_input_${m.id}`}
                                defaultValue={currentRate || ""}
                                className="w-20 p-1.5 border border-slate-350 rounded-lg text-center font-bold bg-white outline-none focus:ring-1 focus:ring-blue-500"
                                placeholder="0.00"
                              />
                              <span className="text-slate-505 font-bold">/ KM</span>
                              <button
                                type="button"
                                onClick={async () => {
                                  const inputEl = document.getElementById(`motoboy_rate_input_${m.id}`);
                                  const newRate = parseFloat(inputEl.value) || 0;
                                  try {
                                    await pb.collection("users").update(m.id, { taxa_motoboy: newRate });
                                    setAllUsers(prev => prev.map(u => u.id === m.id ? { ...u, taxa_motoboy: newRate } : u));
                                    alert(`Taxa do motoboy ${m.name} salva com sucesso como R$ ${newRate.toFixed(2)} / KM!`);
                                  } catch (err) {
                                    console.warn("PocketBase rate update failed, saving in localStorage:", err);
                                    localStorage.setItem(`motoboy_rate_${m.id}`, String(newRate));
                                    setAllUsers(prev => prev.map(u => u.id === m.id ? { ...u, taxa_motoboy: newRate } : u));
                                    alert(`Taxa salva localmente como R$ ${newRate.toFixed(2)} / KM (Erro ao sincronizar com banco de dados).`);
                                  }
                                }}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-[10px] transition-all shadow-sm"
                              >
                                Salvar
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {motoboys.filter(m => m.name !== "MOTOBOY EXTERNO").length === 0 && (
                        <div className="text-center text-slate-400 font-bold py-4">Nenhum motoboy cadastrado.</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* CONFIRM DELIVERY MODAL (For Motoboy) */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">Registrar Entrega Concluída</h3>
              <button onClick={() => setIsConfirmModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleConfirmDelivery} className="p-6 space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-250/60 text-xs font-semibold text-slate-600">
                Pedido: <strong className="text-slate-800">#{activeDelivery?.numero_pedido}</strong>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Nome do Recebedor *</label>
                <input
                  type="text" required
                  value={recebedor}
                  onChange={(e) => setRecebedor(e.target.value)}
                  placeholder="Ex: Esposa do cliente, Porteira"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Taxa da Entrega (R$) *</label>
                <input
                  type="number" required
                  value={taxa}
                  onChange={(e) => setTaxa(e.target.value)}
                  placeholder="Ex: 15.00"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono font-bold text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Foto do Comprovante (Opcional)</label>
                <div className="flex items-center gap-2">
                  <label className="flex-1 flex items-center justify-center gap-1 p-2.5 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 text-xs font-bold text-slate-500">
                    <Camera size={16} /> Tirar Foto / Upload
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setComprovanteFile(e.target.files[0])}
                      className="hidden"
                    />
                  </label>
                  {comprovanteFile && (
                    <span className="text-[10px] text-emerald-600 font-bold border border-emerald-200 bg-emerald-50 px-2 py-1 rounded">
                      Pronto
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Observações</label>
                <textarea
                  value={obs}
                  onChange={(e) => setObs(e.target.value)}
                  rows={2}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsConfirmModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-xs shadow-sm disabled:opacity-50"
                >
                  {isSubmitting ? "Enviando..." : "Confirmar Entrega"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MOTOBOY ASSIGN MODAL (For Admin) */}
      {isAssignModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">Atribuir Motoboy Responsável</h3>
              <button onClick={() => setIsAssignModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <div className="p-6 space-y-3">
              <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600">
                Pedido: <strong className="text-slate-800">#{activeDelivery?.numero_pedido}</strong>
              </div>

              <div className="space-y-1.5">
                {motoboys.map(m => (
                  <button
                    key={m.id}
                    onClick={() => {
                      if (m.name === "MOTOBOY EXTERNO") {
                        setIsAssignModalOpen(false);
                        setIsExternalAssignModalOpen(true);
                      } else {
                        submitAssign(m.id);
                      }
                    }}
                    className="w-full text-left p-2.5 rounded-xl border border-slate-200 hover:bg-blue-50 hover:border-blue-300 font-bold text-xs text-slate-700 transition-all flex items-center justify-between"
                  >
                    <span>{m.name}</span>
                    <ArrowRight size={14} className="text-slate-400" />
                  </button>
                ))}
                
                {motoboys.length === 0 && (
                  <div className="text-center text-xs font-bold text-slate-400 p-4">
                    Nenhum usuário com papel 'motoboy' cadastrado no sistema.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REGISTER PAYMENT MODAL (For Admin) */}
      {isPaymentModalOpen && user?.role === "master" && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">Registrar Pagamento</h3>
              <button onClick={() => setIsPaymentModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleRegisterPayment} className="p-6 space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600">
                Motoboy: <strong className="text-slate-800">{motoboys.find(m => m.id === statementMotoboyId)?.name}</strong>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Valor do Pagamento (R$) *</label>
                <input
                  type="number" required
                  step="any"
                  value={payValue}
                  onChange={(e) => setPayValue(e.target.value)}
                  placeholder={`Valor máximo sugerido: R$ ${statementUnpaidSum}`}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono font-bold text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Observações</label>
                <textarea
                  value={payObs}
                  onChange={(e) => setPayObs(e.target.value)}
                  placeholder="Ex: Transferido via Pix integral referente à semana 22."
                  rows={3}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsPaymentModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  Confirmar Pagamento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MOTOBOY EXTERNAL ASSIGN MODAL (For Admin) */}
      {isExternalAssignModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">Detalhes do Motoboy Externo</h3>
              <button 
                onClick={() => {
                  setIsExternalAssignModalOpen(false);
                  setIsAssignModalOpen(true);
                }} 
                className="text-slate-400 hover:text-slate-600 font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={submitExternalAssign} className="p-6 space-y-4">
              <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600">
                Pedido: <strong className="text-slate-800">#{activeDelivery?.numero_pedido}</strong>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Quem Solicitou? *</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setSolicitadoPor("cliente")}
                    className={`p-2.5 rounded-xl border text-xs font-bold text-center transition-all ${
                      solicitadoPor === "cliente"
                        ? "bg-blue-600 border-blue-600 text-white shadow-sm"
                        : "bg-white border-slate-200 text-slate-650 hover:bg-slate-50"
                    }`}
                  >
                    Cliente
                  </button>
                  <button
                    type="button"
                    onClick={() => setSolicitadoPor("empresa")}
                    className={`p-2.5 rounded-xl border text-xs font-bold text-center transition-all ${
                      solicitadoPor === "empresa"
                        ? "bg-blue-600 border-blue-600 text-white shadow-sm"
                        : "bg-white border-slate-200 text-slate-650 hover:bg-slate-50"
                    }`}
                  >
                    Nós / Empresa
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Observações / Serviço Tercerizado</label>
                <textarea
                  value={externalObs}
                  onChange={(e) => setExternalObs(e.target.value)}
                  placeholder="Ex: Uber Moto entregas, R$ 18.00"
                  rows={3}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-semibold"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setIsExternalAssignModalOpen(false);
                    setIsAssignModalOpen(true);
                  }}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  Atribuir
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MOTOBOY EXTERNAL EDIT/MANAGE MODAL (For Admin) */}
      {isExternalEditModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">Gerenciar Entrega Externa</h3>
              <button 
                onClick={() => setIsExternalEditModalOpen(false)} 
                className="text-slate-400 hover:text-slate-600 font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={submitExternalEdit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto custom-scrollbar">
              <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600">
                Pedido: <strong className="text-slate-800">#{editDelivery?.numero_pedido}</strong>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Status da Entrega *</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold text-xs bg-white cursor-pointer"
                >
                  <option value="pendente">Pendente</option>
                  <option value="saiu_para_entrega">Saiu para Entrega</option>
                  <option value="entregue">Entregue</option>
                  <option value="adiada">Adiada</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Quem Solicitou? *</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setEditSolicitadoPor("cliente")}
                    className={`p-2.5 rounded-xl border text-xs font-bold text-center transition-all ${
                      editSolicitadoPor === "cliente"
                        ? "bg-blue-600 border-blue-600 text-white shadow-sm"
                        : "bg-white border-slate-200 text-slate-650 hover:bg-slate-50"
                    }`}
                  >
                    Cliente
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditSolicitadoPor("empresa")}
                    className={`p-2.5 rounded-xl border text-xs font-bold text-center transition-all ${
                      editSolicitadoPor === "empresa"
                        ? "bg-blue-600 border-blue-600 text-white shadow-sm"
                        : "bg-white border-slate-200 text-slate-650 hover:bg-slate-50"
                    }`}
                  >
                    Nós / Empresa
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Observações / Serviço Terceirizado</label>
                <textarea
                  value={editExternalObs}
                  onChange={(e) => setEditExternalObs(e.target.value)}
                  placeholder="Ex: Uber Moto entregas, R$ 18.00"
                  rows={2}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-semibold"
                />
              </div>

              {editStatus === "entregue" && (
                <div className="space-y-4 pt-3 border-t border-slate-100">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Nome do Recebedor *</label>
                    <input
                      type="text" required
                      value={editRecebedor}
                      onChange={(e) => setEditRecebedor(e.target.value)}
                      placeholder="Ex: Esposa do cliente, Porteiro"
                      className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Taxa da Entrega (R$) *</label>
                    <input
                      type="number" required
                      value={editTaxa}
                      onChange={(e) => setEditTaxa(e.target.value)}
                      placeholder="Ex: 15.00"
                      className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono font-bold text-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Foto do Comprovante (Opcional)</label>
                    <div className="flex items-center gap-2">
                      <label className="flex-1 flex items-center justify-center gap-1 p-2.5 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 text-xs font-bold text-slate-500">
                        <Camera size={16} /> Tirar Foto / Upload
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setEditComprovanteFile(e.target.files[0])}
                          className="hidden"
                        />
                      </label>
                      {editComprovanteFile && (
                        <span className="text-[10px] text-emerald-600 font-bold border border-emerald-200 bg-emerald-50 px-2 py-1 rounded">
                          Pronto
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsExternalEditModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs shadow-sm disabled:opacity-50"
                >
                  {isSubmitting ? "Salvando..." : "Salvar Alterações"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MANUAL INSERT DELIVERY MODAL (For Admin) */}
      {isManualInsertModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up max-h-[90vh] overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                <Bike className="text-blue-600" size={18} /> Inclusão Manual de Solicitação de Entrega
              </h3>
              <button onClick={() => setIsManualInsertModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleCreateManualDelivery} className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
              {/* Informações Gerais */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/60 space-y-4">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Informações Gerais</h4>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Nº Pedido *</label>
                    <input
                      type="text" required
                      value={manualOrderNumber}
                      onChange={(e) => setManualOrderNumber(e.target.value)}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Cliente/Destinatário *</label>
                    <input
                      type="text" required
                      value={manualCustomerName}
                      onChange={(e) => setManualCustomerName(e.target.value)}
                      placeholder="Ex: Nome do cliente"
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Solicitante *</label>
                    <select
                      value={manualSolicitanteId}
                      onChange={(e) => setManualSolicitanteId(e.target.value)}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold bg-white"
                    >
                      <option value="">Selecione...</option>
                      {allUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.name || u.username}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Motoboy Atribuído</label>
                    <select
                      value={manualMotoboyId}
                      onChange={(e) => setManualMotoboyId(e.target.value)}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold bg-white"
                    >
                      <option value="">Nenhum (Pendente)</option>
                      {motoboys.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Endereço de Retirada */}
              <div className="border border-slate-200/80 rounded-xl p-4 space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1">
                    <MapPin size={14} className="text-amber-500" /> Endereço de Retirada (Origem)
                  </h4>
                  <div className="flex items-center gap-2">
                    {savedDestinations.length > 0 && (
                      <select
                        onChange={(e) => {
                          const dest = savedDestinations.find(d => d.id === e.target.value);
                          if (dest && dest.endereco) {
                            setManualRetiradaAddress(dest.endereco);
                          }
                        }}
                        defaultValue=""
                        className="text-[10px] border border-slate-350 rounded-lg p-1 font-bold text-slate-700 outline-none max-w-[150px] bg-white cursor-pointer"
                      >
                        <option value="">Carregar Salvo...</option>
                        {savedDestinations.map(d => (
                          <option key={d.id} value={d.id}>{d.nome}</option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={() => handleSaveFrequentDestination("retirada", false)}
                      className="text-[10px] bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 px-2 py-1 rounded-lg font-bold transition-all shadow-sm flex items-center gap-1"
                    >
                      💾 Salvar
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsManageDestinationsModalOpen(true)}
                      className="text-[10px] bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200 px-2 py-1 rounded-lg font-bold transition-all shadow-sm flex items-center gap-1"
                    >
                      ⚙️ Gerenciar
                    </button>
                    <button
                      type="button"
                      onClick={() => setManualRetiradaAddress(OFFICE_ADDRESS)}
                      className="text-[10px] bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 px-2.5 py-1 rounded-lg font-bold transition-all shadow-sm flex items-center gap-1"
                    >
                      🏢 Escritório
                    </button>
                    <button
                      type="button"
                      onClick={() => setManualRetiradaAddress(WORKSHOP_ADDRESS)}
                      className="text-[10px] bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200 px-2.5 py-1 rounded-lg font-bold transition-all shadow-sm flex items-center gap-1"
                    >
                      🛠️ Oficina
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 text-xs">
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">CEP</label>
                    <input
                      type="text"
                      value={manualRetiradaAddress.cep}
                      onChange={(e) => {
                        const val = e.target.value;
                        setManualRetiradaAddress(p => ({ ...p, cep: val }));
                      }}
                      onBlur={(e) => fetchAddressByCep(e.target.value, "retirada")}
                      placeholder="00000-000"
                      className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Rua / Logradouro</label>
                    <input
                      type="text"
                      value={manualRetiradaAddress.logradouro}
                      onChange={(e) => setManualRetiradaAddress(p => ({ ...p, logradouro: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Número</label>
                    <input
                      type="text"
                      value={manualRetiradaAddress.numero}
                      onChange={(e) => setManualRetiradaAddress(p => ({ ...p, numero: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Bairro</label>
                    <input
                      type="text"
                      value={manualRetiradaAddress.bairro}
                      onChange={(e) => setManualRetiradaAddress(p => ({ ...p, bairro: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Complemento</label>
                    <input
                      type="text"
                      value={manualRetiradaAddress.complemento}
                      onChange={(e) => setManualRetiradaAddress(p => ({ ...p, complemento: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Cidade</label>
                    <input
                      type="text"
                      value={manualRetiradaAddress.cidade}
                      onChange={(e) => setManualRetiradaAddress(p => ({ ...p, cidade: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Estado (UF)</label>
                    <input
                      type="text"
                      maxLength={2}
                      value={manualRetiradaAddress.estado}
                      onChange={(e) => setManualRetiradaAddress(p => ({ ...p, estado: e.target.value.toUpperCase() }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Endereço de Entrega */}
              <div className="border border-slate-200/80 rounded-xl p-4 space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1">
                    <MapPin size={14} className="text-blue-500" /> Endereço de Entrega (Destino)
                  </h4>
                  <div className="flex items-center gap-2">
                    {savedDestinations.length > 0 && (
                      <select
                        onChange={(e) => {
                          const dest = savedDestinations.find(d => d.id === e.target.value);
                          if (dest && dest.endereco) {
                            setManualEntregaAddress(dest.endereco);
                          }
                        }}
                        defaultValue=""
                        className="text-[10px] border border-slate-350 rounded-lg p-1 font-bold text-slate-700 outline-none max-w-[150px] bg-white cursor-pointer"
                      >
                        <option value="">Carregar Salvo...</option>
                        {savedDestinations.map(d => (
                          <option key={d.id} value={d.id}>{d.nome}</option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={() => handleSaveFrequentDestination("entrega", false)}
                      className="text-[10px] bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 px-2.5 py-1 rounded-lg font-bold transition-all shadow-sm flex items-center gap-1"
                    >
                      💾 Salvar
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsManageDestinationsModalOpen(true)}
                      className="text-[10px] bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-lg font-bold transition-all shadow-sm flex items-center gap-1"
                    >
                      ⚙️ Gerenciar
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 text-xs">
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">CEP</label>
                    <input
                      type="text"
                      value={manualEntregaAddress.cep}
                      onChange={(e) => {
                        const val = e.target.value;
                        setManualEntregaAddress(p => ({ ...p, cep: val }));
                      }}
                      onBlur={(e) => fetchAddressByCep(e.target.value, "entrega")}
                      placeholder="00000-000"
                      className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Rua / Logradouro</label>
                    <input
                      type="text"
                      value={manualEntregaAddress.logradouro}
                      onChange={(e) => setManualEntregaAddress(p => ({ ...p, logradouro: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Número</label>
                    <input
                      type="text"
                      value={manualEntregaAddress.numero}
                      onChange={(e) => setManualEntregaAddress(p => ({ ...p, numero: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Bairro</label>
                    <input
                      type="text"
                      value={manualEntregaAddress.bairro}
                      onChange={(e) => setManualEntregaAddress(p => ({ ...p, bairro: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Complemento</label>
                    <input
                      type="text"
                      value={manualEntregaAddress.complemento}
                      onChange={(e) => setManualEntregaAddress(p => ({ ...p, complemento: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Cidade</label>
                    <input
                      type="text"
                      value={manualEntregaAddress.cidade}
                      onChange={(e) => setManualEntregaAddress(p => ({ ...p, cidade: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Estado (UF)</label>
                    <input
                      type="text"
                      maxLength={2}
                      value={manualEntregaAddress.estado}
                      onChange={(e) => setManualEntregaAddress(p => ({ ...p, estado: e.target.value.toUpperCase() }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Valores e Tarifas */}
              <div className="bg-blue-50/40 p-4 rounded-xl border border-blue-200/60 space-y-3">
                <h4 className="text-xs font-bold text-blue-700 uppercase tracking-wider flex items-center gap-1">
                  <DollarSign size={14} /> Valores e Tarifas
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1">
                      Distância (KM) {isCalculatingDistance && <span className="text-blue-500 animate-pulse">(Calculando...)</span>}
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={manualDistanceKm || ""}
                      onChange={(e) => handleDistanceChange(e.target.value, "manual")}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold text-xs bg-white"
                      placeholder="Aguardando endereços..."
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Valor Sugerido</label>
                    <div className="w-full p-2 bg-slate-100 border border-slate-200 rounded-lg font-bold text-slate-700 text-xs h-[34px] flex items-center">
                      {manualSuggestedValue > 0 ? `R$ ${manualSuggestedValue.toFixed(2)}` : "R$ 0.00"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Taxa de Entrega Final (R$) *</label>
                    <input
                      type="number"
                      value={manualTaxa || ""}
                      onChange={(e) => setManualTaxa(e.target.value)}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold text-xs bg-white"
                      placeholder="Ex: 15"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-blue-200/50 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={manualIsDiretaUrgente}
                      onChange={(e) => handleToggleDiretaUrgente(e.target.checked, "manual")}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500 cursor-pointer"
                    />
                    <span className="text-xs font-bold text-blue-700 flex items-center gap-1">
                      ⚡ ENTREGA DIRETA / URGENTE (Ida e Volta - Dobrar Valor Sugerido)
                    </span>
                  </label>
                </div>
              </div>

              {/* Observações */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Observações</label>
                <textarea
                  value={manualObs}
                  onChange={(e) => setManualObs(e.target.value)}
                  placeholder="Instruções adicionais da entrega..."
                  rows={2}
                  className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-semibold"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsManualInsertModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs shadow-sm disabled:opacity-50"
                >
                  {isSubmitting ? "Cadastrando..." : "Cadastrar Solicitação"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* GENERAL EDIT DELIVERY MODAL */}
      {isGeneralEditModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up max-h-[90vh] overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                <Edit className="text-blue-600" size={18} /> Editar Solicitação de Entrega
              </h3>
              <button onClick={() => setIsGeneralEditModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleUpdateGeneralDelivery} className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
              {/* Informações Gerais */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/60 space-y-4">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Informações Gerais</h4>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Nº Pedido *</label>
                    <input
                      type="text" required
                      value={generalEditOrderNumber}
                      onChange={(e) => setGeneralEditOrderNumber(e.target.value)}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Cliente/Destinatário *</label>
                    <input
                      type="text" required
                      value={generalEditCustomerName}
                      onChange={(e) => setGeneralEditCustomerName(e.target.value)}
                      placeholder="Ex: Nome do cliente"
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Solicitante *</label>
                    <select
                      value={generalEditSolicitanteId}
                      onChange={(e) => setGeneralEditSolicitanteId(e.target.value)}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold bg-white"
                    >
                      <option value="">Selecione...</option>
                      {allUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.name || u.username}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Motoboy</label>
                    <select
                      value={generalEditMotoboyId}
                      onChange={(e) => setGeneralEditMotoboyId(e.target.value)}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold bg-white"
                    >
                      <option value="">Nenhum (Pendente)</option>
                      {motoboys.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Status *</label>
                    <select
                      value={generalEditStatus}
                      onChange={(e) => setGeneralEditStatus(e.target.value)}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold bg-white"
                    >
                      <option value="pendente">Pendente</option>
                      <option value="saiu_para_entrega">Saiu para Entrega</option>
                      <option value="entregue">Entregue</option>
                      <option value="adiada">Adiada</option>
                      <option value="cancelada">Cancelada</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Endereço de Retirada */}
              <div className="border border-slate-200/80 rounded-xl p-4 space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1">
                    <MapPin size={14} className="text-amber-500" /> Endereço de Retirada (Origem)
                  </h4>
                  <div className="flex items-center gap-2">
                    {savedDestinations.length > 0 && (
                      <select
                        onChange={(e) => {
                          const dest = savedDestinations.find(d => d.id === e.target.value);
                          if (dest && dest.endereco) {
                            setGeneralEditRetiradaAddress(dest.endereco);
                          }
                        }}
                        defaultValue=""
                        className="text-[10px] border border-slate-350 rounded-lg p-1 font-bold text-slate-700 outline-none max-w-[150px] bg-white cursor-pointer"
                      >
                        <option value="">Carregar Salvo...</option>
                        {savedDestinations.map(d => (
                          <option key={d.id} value={d.id}>{d.nome}</option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={() => handleSaveFrequentDestination("retirada", true)}
                      className="text-[10px] bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 px-2 py-1 rounded-lg font-bold transition-all shadow-sm flex items-center gap-1"
                    >
                      💾 Salvar
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsManageDestinationsModalOpen(true)}
                      className="text-[10px] bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200 px-2 py-1 rounded-lg font-bold transition-all shadow-sm flex items-center gap-1"
                    >
                      ⚙️ Gerenciar
                    </button>
                    <button
                      type="button"
                      onClick={() => setGeneralEditRetiradaAddress(OFFICE_ADDRESS)}
                      className="text-[10px] bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 px-2.5 py-1 rounded-lg font-bold transition-all shadow-sm flex items-center gap-1"
                    >
                      🏢 Escritório
                    </button>
                    <button
                      type="button"
                      onClick={() => setGeneralEditRetiradaAddress(WORKSHOP_ADDRESS)}
                      className="text-[10px] bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200 px-2.5 py-1 rounded-lg font-bold transition-all shadow-sm flex items-center gap-1"
                    >
                      🛠️ Oficina
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 text-xs">
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">CEP</label>
                    <input
                      type="text"
                      value={generalEditRetiradaAddress.cep}
                      onChange={(e) => {
                        const val = e.target.value;
                        setGeneralEditRetiradaAddress(p => ({ ...p, cep: val }));
                      }}
                      onBlur={(e) => fetchAddressByCepForEdit(e.target.value, "retirada")}
                      placeholder="00000-000"
                      className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Rua / Logradouro</label>
                    <input
                      type="text"
                      value={generalEditRetiradaAddress.logradouro}
                      onChange={(e) => setGeneralEditRetiradaAddress(p => ({ ...p, logradouro: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Número</label>
                    <input
                      type="text"
                      value={generalEditRetiradaAddress.numero}
                      onChange={(e) => setGeneralEditRetiradaAddress(p => ({ ...p, numero: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Bairro</label>
                    <input
                      type="text"
                      value={generalEditRetiradaAddress.bairro}
                      onChange={(e) => setGeneralEditRetiradaAddress(p => ({ ...p, bairro: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Complemento</label>
                    <input
                      type="text"
                      value={generalEditRetiradaAddress.complemento}
                      onChange={(e) => setGeneralEditRetiradaAddress(p => ({ ...p, complemento: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Cidade</label>
                    <input
                      type="text"
                      value={generalEditRetiradaAddress.cidade}
                      onChange={(e) => setGeneralEditRetiradaAddress(p => ({ ...p, cidade: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Estado (UF)</label>
                    <input
                      type="text"
                      maxLength={2}
                      value={generalEditRetiradaAddress.estado}
                      onChange={(e) => setGeneralEditRetiradaAddress(p => ({ ...p, estado: e.target.value.toUpperCase() }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Endereço de Entrega */}
              <div className="border border-slate-200/80 rounded-xl p-4 space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1">
                    <MapPin size={14} className="text-blue-500" /> Endereço de Entrega (Destino)
                  </h4>
                  <div className="flex items-center gap-2">
                    {savedDestinations.length > 0 && (
                      <select
                        onChange={(e) => {
                          const dest = savedDestinations.find(d => d.id === e.target.value);
                          if (dest && dest.endereco) {
                            setGeneralEditEntregaAddress(dest.endereco);
                          }
                        }}
                        defaultValue=""
                        className="text-[10px] border border-slate-350 rounded-lg p-1 font-bold text-slate-700 outline-none max-w-[150px] bg-white cursor-pointer"
                      >
                        <option value="">Carregar Salvo...</option>
                        {savedDestinations.map(d => (
                          <option key={d.id} value={d.id}>{d.nome}</option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={() => handleSaveFrequentDestination("entrega", true)}
                      className="text-[10px] bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 px-2 py-1 rounded-lg font-bold transition-all shadow-sm flex items-center gap-1"
                    >
                      💾 Salvar
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsManageDestinationsModalOpen(true)}
                      className="text-[10px] bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-lg font-bold transition-all shadow-sm flex items-center gap-1"
                    >
                      ⚙️ Gerenciar
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 text-xs">
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">CEP</label>
                    <input
                      type="text"
                      value={generalEditEntregaAddress.cep}
                      onChange={(e) => {
                        const val = e.target.value;
                        setGeneralEditEntregaAddress(p => ({ ...p, cep: val }));
                      }}
                      onBlur={(e) => fetchAddressByCepForEdit(e.target.value, "entrega")}
                      placeholder="00000-000"
                      className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Rua / Logradouro</label>
                    <input
                      type="text"
                      value={generalEditEntregaAddress.logradouro}
                      onChange={(e) => setGeneralEditEntregaAddress(p => ({ ...p, logradouro: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Número</label>
                    <input
                      type="text"
                      value={generalEditEntregaAddress.numero}
                      onChange={(e) => setGeneralEditEntregaAddress(p => ({ ...p, numero: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Bairro</label>
                    <input
                      type="text"
                      value={generalEditEntregaAddress.bairro}
                      onChange={(e) => setGeneralEditEntregaAddress(p => ({ ...p, bairro: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Complemento</label>
                    <input
                      type="text"
                      value={generalEditEntregaAddress.complemento}
                      onChange={(e) => setGeneralEditEntregaAddress(p => ({ ...p, complemento: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Cidade</label>
                    <input
                      type="text"
                      value={generalEditEntregaAddress.cidade}
                      onChange={(e) => setGeneralEditEntregaAddress(p => ({ ...p, cidade: e.target.value }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Estado (UF)</label>
                    <input
                      type="text"
                      maxLength={2}
                      value={generalEditEntregaAddress.estado}
                      onChange={(e) => setGeneralEditEntregaAddress(p => ({ ...p, estado: e.target.value.toUpperCase() }))}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Valores e Tarifas */}
              <div className="bg-blue-50/40 p-4 rounded-xl border border-blue-200/60 space-y-3">
                <h4 className="text-xs font-bold text-blue-700 uppercase tracking-wider flex items-center gap-1">
                  <DollarSign size={14} /> Valores e Tarifas
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1">
                      Distância (KM) {isCalculatingDistanceEdit && <span className="text-blue-500 animate-pulse">(Calculando...)</span>}
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={generalEditDistanceKm || ""}
                      onChange={(e) => handleDistanceChange(e.target.value, "edit")}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold text-xs bg-white"
                      placeholder="Aguardando endereços..."
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Valor Sugerido</label>
                    <div className="w-full p-2 bg-slate-100 border border-slate-200 rounded-lg font-bold text-slate-700 text-xs h-[34px] flex items-center">
                      {generalEditSuggestedValue > 0 ? `R$ ${generalEditSuggestedValue.toFixed(2)}` : "R$ 0.00"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Taxa de Entrega Final (R$) *</label>
                    <input
                      type="number"
                      value={generalEditTaxa || ""}
                      onChange={(e) => setGeneralEditTaxa(e.target.value)}
                      className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold text-xs bg-white"
                      placeholder="Ex: 15"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-blue-200/50 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={generalEditIsDiretaUrgente}
                      onChange={(e) => handleToggleDiretaUrgente(e.target.checked, "edit")}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500 cursor-pointer"
                    />
                    <span className="text-xs font-bold text-blue-700 flex items-center gap-1">
                      ⚡ ENTREGA DIRETA / URGENTE (Ida e Volta - Dobrar Valor Sugerido)
                    </span>
                  </label>
                </div>
              </div>

              {/* Informações da Entrega Concluída */}
              {generalEditStatus === "entregue" && (
                <div className="bg-emerald-50/40 p-4 rounded-xl border border-emerald-200/50 space-y-4 animate-scale-up">
                  <h4 className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-2">Dados da Entrega Concluída</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Recebedor *</label>
                      <input
                        type="text" required
                        value={generalEditRecebedor}
                        onChange={(e) => setGeneralEditRecebedor(e.target.value)}
                        placeholder="Ex: Porteiro, Cônjuge"
                        className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-emerald-500 text-xs font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Taxa cobrada (R$) *</label>
                      <input
                        type="number" required
                        value={generalEditTaxa}
                        onChange={(e) => setGeneralEditTaxa(e.target.value)}
                        placeholder="Ex: 15.00"
                        className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-emerald-500 text-xs font-mono font-bold"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Observações */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Observações</label>
                <textarea
                  value={generalEditObs}
                  onChange={(e) => setGeneralEditObs(e.target.value)}
                  placeholder="Instruções ou notas do serviço..."
                  rows={2}
                  className="w-full p-2 border border-slate-350 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-semibold"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsGeneralEditModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs shadow-sm disabled:opacity-50"
                >
                  {isSubmitting ? "Salvando..." : "Salvar Alterações"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isManageDestinationsModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full shadow-2xl border border-slate-200 flex flex-col max-h-[80vh] overflow-hidden animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                <MapPin className="text-blue-600" size={18} /> Gerenciar Endereços Salvos
              </h3>
              <button onClick={() => setIsManageDestinationsModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
              {savedDestinations.length === 0 ? (
                <div className="text-center text-slate-400 py-8 font-semibold">Nenhum endereço salvo.</div>
              ) : (
                <div className="space-y-3">
                  {savedDestinations.map(d => (
                    <div key={d.id} className="p-3 border border-slate-200 rounded-xl bg-slate-50 flex justify-between items-start gap-4">
                      {editingDestinationId === d.id ? (
                        <div className="flex-1 space-y-2 text-xs">
                          <div>
                            <label className="block text-[9px] font-bold text-slate-500 uppercase mb-0.5">Nome de Identificação</label>
                            <input
                              type="text"
                              value={editDestForm.nome}
                              onChange={(e) => setEditDestForm(p => ({ ...p, nome: e.target.value }))}
                              className="w-full p-1.5 border border-slate-300 rounded-lg font-bold outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="block text-[9px] font-bold text-slate-500 uppercase mb-0.5">CEP</label>
                              <input
                                type="text"
                                value={editDestForm.endereco.cep}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setEditDestForm(p => ({ ...p, endereco: { ...p.endereco, cep: val } }));
                                }}
                                onBlur={async (e) => {
                                  const cleanCep = e.target.value.replace(/\D/g, "");
                                  if (cleanCep.length === 8) {
                                    try {
                                      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
                                      const resData = await response.json();
                                      if (!resData.erro) {
                                        setEditDestForm(p => ({
                                          ...p,
                                          endereco: {
                                            ...p.endereco,
                                            logradouro: resData.logradouro || "",
                                            bairro: resData.bairro || "",
                                            cidade: resData.localidade || "",
                                            estado: resData.uf || "",
                                            cep: resData.cep || e.target.value
                                          }
                                        }));
                                      }
                                    } catch (err) {
                                      console.error(err);
                                    }
                                  }
                                }}
                                className="w-full p-1.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-[9px] font-bold text-slate-500 uppercase mb-0.5">Rua</label>
                              <input
                                type="text"
                                value={editDestForm.endereco.logradouro}
                                onChange={(e) => setEditDestForm(p => ({ ...p, endereco: { ...p.endereco, logradouro: e.target.value } }))}
                                className="w-full p-1.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="block text-[9px] font-bold text-slate-500 uppercase mb-0.5">Número</label>
                              <input
                                type="text"
                                value={editDestForm.endereco.numero}
                                onChange={(e) => setEditDestForm(p => ({ ...p, endereco: { ...p.endereco, numero: e.target.value } }))}
                                className="w-full p-1.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-[9px] font-bold text-slate-500 uppercase mb-0.5">Bairro</label>
                              <input
                                type="text"
                                value={editDestForm.endereco.bairro}
                                onChange={(e) => setEditDestForm(p => ({ ...p, endereco: { ...p.endereco, bairro: e.target.value } }))}
                                className="w-full p-1.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-[9px] font-bold text-slate-500 uppercase mb-0.5">Compl.</label>
                              <input
                                type="text"
                                value={editDestForm.endereco.complemento}
                                onChange={(e) => setEditDestForm(p => ({ ...p, endereco: { ...p.endereco, complemento: e.target.value } }))}
                                className="w-full p-1.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div className="col-span-2">
                              <label className="block text-[9px] font-bold text-slate-500 uppercase mb-0.5">Cidade</label>
                              <input
                                type="text"
                                value={editDestForm.endereco.cidade}
                                onChange={(e) => setEditDestForm(p => ({ ...p, endereco: { ...p.endereco, cidade: e.target.value } }))}
                                className="w-full p-1.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-[9px] font-bold text-slate-500 uppercase mb-0.5">Estado</label>
                              <input
                                type="text"
                                maxLength={2}
                                value={editDestForm.endereco.estado}
                                onChange={(e) => setEditDestForm(p => ({ ...p, endereco: { ...p.endereco, estado: e.target.value.toUpperCase() } }))}
                                className="w-full p-1.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2 justify-end">
                            <button
                              type="button"
                              onClick={handleSaveEditDestination}
                              className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold text-[10px]"
                            >
                              Salvar
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingDestinationId(null)}
                              className="px-3 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded font-bold text-[10px]"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="text-xs">
                            <div className="font-bold text-slate-800">{d.nome}</div>
                            <div className="text-slate-505 text-[11px] mt-0.5">
                              {d.endereco?.logradouro}, {d.endereco?.numero} {d.endereco?.complemento && `(${d.endereco?.complemento})`} - {d.endereco?.bairro}, {d.endereco?.cidade}/{d.endereco?.estado}
                            </div>
                            <div className="text-slate-400 text-[10px] mt-0.5">CEP: {d.endereco?.cep}</div>
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            <button
                              type="button"
                              onClick={() => handleStartEditDestination(d)}
                              className="p-1.5 bg-white hover:bg-blue-50 border border-slate-200 text-blue-600 rounded-lg transition-all"
                              title="Editar"
                            >
                              <Edit size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteDestination(d.id)}
                              className="p-1.5 bg-white hover:bg-red-50 border border-slate-200 text-red-500 rounded-lg transition-all"
                              title="Excluir"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-100 flex justify-end shrink-0">
              <button
                onClick={() => setIsManageDestinationsModalOpen(false)}
                className="px-4 py-2 bg-slate-150 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-xs"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
